import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { isDeliveryType, isPickupType, displayBookingType } from '../../../../constants/bookingTypes';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import EditBooking from '../EditBooking';
import './scheduling.css';
import SchedulingPermits from './SchedulingPermits';
import ScheduleCopyButton from './ScheduleCopyButton';
import RouteDialog from './RouteDialog';
import { formatDubai, todayDubaiYMD } from '../../../../utils/dubaiTime';

import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { colorOf, AREA_PRIORITY, ROUTE_GROUPS } from '../../../../constants/areas';

function Scheduling({ customDate }) {
    const { date: routeDate } = useParams();
    const navigate = useNavigate();
    // Default day = today in DUBAI (item 8) — toISOString() gave the UTC day,
    // which is yesterday between midnight and 04:00 Dubai time.
    const [selectedDate, setSelectedDate] = useState(
        customDate || routeDate || todayDubaiYMD()
    );

    useEffect(() => {
        setSelectedDate(customDate || routeDate || todayDubaiYMD());
    }, [customDate, routeDate]);

    const [availabilities, setAvailabilities] = useState([]); // raw rows from API
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cleanerSearch, setCleanerSearch] = useState('');

    // Which route to show: 'all' | <route id>
    // Was a fixed 'both' | 'group1' | 'group2' — routes are now data, so a
    // third or fourth driver needs no code change. Spec: PLAN_driver_routing.md
    const [activeRouteId, setActiveRouteId] = useState('all');

    // Driver routes for the day. Each one is what we copy into WhatsApp and
    // what that driver sees in their app — the same stops, so the two can
    // never disagree.
    const [routes, setRoutes] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [routeByBooking, setRouteByBooking] = useState({}); // bookingId -> route id
    // Route ids currently showing the driver's stop list instead of the cards.
    const [driverViewRoutes, setDriverViewRoutes] = useState(() => new Set());
    // bookingId -> its legs, so each card can say who has been dropped and when.
    const [stopsByBooking, setStopsByBooking] = useState({});
    // Until routes are known, EVERY booking would look unassigned and land in
    // one merged list. Hold the sections back rather than flash a wrong layout.
    const [routesLoaded, setRoutesLoaded] = useState(false);
    // { mode: 'add' } | { mode: 'remove', route } — a real dialog, not a
    // browser prompt that cannot explain what removal does to the bookings.
    const [routeDialog, setRouteDialog] = useState(null);
    const [routeDialogBusy, setRouteDialogBusy] = useState(false);
    // Any other route mutation (driver assign, moving a booking) so the top
    // line reports it too — the page must never look idle while it is working.
    const [routeBusy, setRouteBusy] = useState(false);
    // Which booking's "move to route" menu is open (booking id, or null).
    const [moveMenuFor, setMoveMenuFor] = useState(null);
    const [routingError, setRoutingError] = useState('');


    const [isCreatingPartTimer, setIsCreatingPartTimer] = useState(false);
    const [partTimeOptions, setPartTimeOptions] = useState([]);
    const [selectedPartTimer, setSelectedPartTimer] = useState(null);
    const [copiedBookingId, setCopiedBookingId] = useState(null);

    // bookingId -> array of cleaner slots { id, availability_id, cleaner_id, name }
    const [assignments, setAssignments] = useState({});

    // Which time window we’re currently scheduling
    // 'morning' | 'peak' | 'afternoon' | 'all'
    const [activeTimeGroup, setActiveTimeGroup] = useState('all');

    // Show / hide cleaners panel on the right
    const [showRightPanel, setShowRightPanel] = useState(false);

    // NEW: which booking are we editing?
    const [editBookingId, setEditBookingId] = useState(null);

    const [permitModalBooking, setPermitModalBooking] = useState(null);

    // The same two groups the driver routes are seeded from — one list, in
    // constants/areas.js, so the board and the router can never disagree about
    // which car an area belongs to.
    const group1Locations = ROUTE_GROUPS.Downtown;
    const group2Locations = ROUTE_GROUPS.Marina;


    // Everything that needs a cleaner (and so a car) today. The route sections
    // below all draw from this, so a booking can never be filtered out of one
    // route and quietly appear in another.
    const schedulableBookings = bookings.filter(
        (b) =>
            b.booking_type !== 'Laundry Service Only' &&
            b.status !== 'Last Minute Cancelled' &&
            b.status !== 'Last Minute Rescheduled'
    );

    const bookingsGroup1 = bookings.filter(
        (b) =>
            group1Locations.includes(b.area) &&
            b.booking_type !== 'Laundry Service Only' &&
            b.status !== 'Last Minute Cancelled' &&
            b.status !== 'Last Minute Rescheduled'
    );

    const bookingsGroup2 = bookings.filter(
        (b) =>
            group2Locations.includes(b.area) &&
            b.booking_type !== 'Laundry Service Only' &&
            b.status !== 'Last Minute Cancelled' &&
            b.status !== 'Last Minute Rescheduled'
    );

    // 👇 same as DailyBookings
    // The far side of town first — one shared order, see constants/areas.js.
    const areaPriorityOrder = AREA_PRIORITY;

    // 👇 Sort bookings inside a group/time window by:
    // 1) earliest start time per area
    // 2) area priority (for Marina/JBR/JLT/Palm/JVC) when times are equal
    const sortBookingsByAreaAndTime = (bookingsForGroupAndTime) => {
        if (!bookingsForGroupAndTime || bookingsForGroupAndTime.length === 0) return [];

        // Group by area
        const byArea = bookingsForGroupAndTime.reduce((acc, b) => {
            const key = b.area || 'Other Area';
            if (!acc[key]) acc[key] = [];
            acc[key].push(b);
            return acc;
        }, {});

        // Helper: earliest valid start time in a list
        const getEarliest = (list) => {
            const times = list
                .map((b) => parseTime(b.start_time))
                .filter((t) => !isNaN(t));
            if (!times.length) return Infinity;
            return Math.min(...times);
        };

        // Sort areas by earliest booking time, then by area priority
        const sortedAreas = Object.keys(byArea).sort((a, b) => {
            const tA = getEarliest(byArea[a]);
            const tB = getEarliest(byArea[b]);

            if (tA !== tB) {
                return tA - tB; // earlier area first
            }

            // Same earliest time → tie-break with areaPriorityOrder
            const idxA = areaPriorityOrder.indexOf(a);
            const idxB = areaPriorityOrder.indexOf(b);

            // If both are not in priority list, keep relative order (0)
            if (idxA === -1 && idxB === -1) return 0;
            if (idxA === -1) return 1;
            if (idxB === -1) return -1;
            return idxA - idxB;
        });

        // Flatten back to a list, sorted inside each area by start time
        const result = [];
        sortedAreas.forEach((area) => {
            const sortedInsideArea = [...byArea[area]].sort((b1, b2) => {
                const t1 = parseTime(b1.start_time);
                const t2 = parseTime(b2.start_time);
                if (isNaN(t1) && isNaN(t2)) return 0;
                if (isNaN(t1)) return 1;
                if (isNaN(t2)) return -1;
                return t1 - t2;
            });
            result.push(...sortedInsideArea);
        });

        return result;
    };

    const SERVICE_FLAG_TYPE_IDS = new Set([2, 3, 4, 9, 10]);

    const SERVICE_FLAG_TYPE_NAMES = new Set([
        'Hourly Cleaning with Materials',
        'Hourly Cleaning without Materials',
        'Deep Cleaning',
        'Touchup Cleaning (Free)',
        'Minimum Call Out',
    ]);

    const shouldShowServiceFlags = (b) => {
        const id = Number(b.booking_type_id ?? b.booking_typeId ?? b.booking_type_id_fk);
        if (!Number.isNaN(id) && SERVICE_FLAG_TYPE_IDS.has(id)) return true;

        const name = (b.booking_type || '').trim();
        return SERVICE_FLAG_TYPE_NAMES.has(name);
    };

    const formatYesNo = (v) => (v ? 'Yes' : 'No');

    const parseTime = useCallback((timeString) => {
        if (!timeString) return NaN;
        const timeMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!timeMatch) return NaN;
        let [, hours, minutes, period] = timeMatch;
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);
        if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
        else if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
        return hours + minutes / 60;
    }, []);

    const getAreaColor = (area) => {

        return colorOf(area);
    };

    // Build status / type badges for a booking (similar to DailyBookings)
    const getBookingBadges = (b) => {
        const badges = [];

        // Type-based badges
        if (isDeliveryType(b.booking_type)) {
            badges.push({
                key: 'type-delivery',
                text: 'Delivery Only',
                className: 'scheduling-badge scheduling-badge--orange',
            });
        } else if (isPickupType(b.booking_type)) {
            badges.push({
                key: 'type-pickup',
                text: 'Pickup Only',
                className: 'scheduling-badge scheduling-badge--orange',
            });
        } else if (b.booking_type === 'Laundry Service Only') {
            badges.push({
                key: 'type-laundry',
                text: 'Laundry',
                className: 'scheduling-badge scheduling-badge--laundry',
            });
        }

        // Status-based badges
        if (b.status === 'Last Minute Cancelled' || b.status === 'Last Minute Rescheduled') {
            badges.push({
                key: 'status-cancelled',
                text: 'Cancelled',
                className: 'scheduling-badge scheduling-badge--red',
            });
        } else if (b.status === 'Pending') {
            badges.push({
                key: 'status-pending',
                text: 'Pending',
                className: 'scheduling-badge scheduling-badge--pending',
            });
        } else if (b.status === 'Completed') {
            badges.push({
                key: 'status-completed',
                text: 'Completed',
                className: 'scheduling-badge scheduling-badge--completed',
            });
        } else if (b.status === 'In Process') {
            badges.push({
                key: 'status-in-process',
                text: 'Cleaning Started..',
                className: 'scheduling-badge scheduling-badge--in-process',
            });
        } else if (b.status === 'Confirmed') {
            badges.push({
                key: 'status-confirmed',
                text: 'Confirmed',
                className: 'scheduling-badge scheduling-badge--confirmed',
            });
        }

        return badges;
    };

    const renderBookingBadges = (b) => {
        const badges = getBookingBadges(b);
        if (!badges.length) return null;

        return (
            <div className="scheduling-booking-status">
                {badges.map((badge) => (
                    <span key={badge.key} className={badge.className}>
                        {badge.text}
                    </span>
                ))}
            </div>
        );
    };

    const renderAccessRequirementBadges = (b) => {
        const {
            is_id_required,
            is_company_id_required,
            is_accompany_id_required,
            is_permit_needed,
            has_permit,
        } = b;

        const badges = [];

        // 🔐 ID requirements
        if (is_company_id_required) {
            badges.push({
                key: 'id-company',
                text: 'Company ID Required',
                className: 'scheduling-access-badge scheduling-access-badge--company-id',
            });
        } else if (is_id_required) {
            badges.push({
                key: 'id-any',
                text: 'ID Required',
                className: 'scheduling-access-badge scheduling-access-badge--id',
            });
        } else if (is_accompany_id_required) {
            badges.push({
                key: 'id-one',
                text: '1+ ID Needed',
                className: 'scheduling-access-badge scheduling-access-badge--one-id',
            });
        }

        // 📄 Permit – open modal to view/upload/remove
        if (is_permit_needed) {
            const baseClass = 'scheduling-permit-badge ';

            badges.push({
                key: has_permit ? 'permit-ok' : 'permit-missing',
                text: has_permit ? 'Permit Uploaded' : 'Permit Required +',
                className:
                    baseClass +
                    (has_permit
                        ? 'scheduling-permit-badge--ok'
                        : 'scheduling-permit-badge--missing'),
                onClick: () => setPermitModalBooking(b),
            });
        }

        if (!badges.length) return null;

        return (
            <div className="scheduling-access-badges">
                {badges.map((badge) => {
                    if (badge.onClick) {
                        return (
                            <button
                                key={badge.key}
                                type="button"
                                className={badge.className + ' scheduling-permit-badge--button'}
                                onClick={badge.onClick}
                            >
                                {badge.text}
                            </button>
                        );
                    }

                    return (
                        <span key={badge.key} className={badge.className}>
                            {badge.text}
                        </span>
                    );
                })}
            </div>
        );
    };

    // Map availability_id -> availability row
    const availabilityMap = useMemo(() => {
        const map = new Map();
        availabilities.forEach((a) => {
            if (a.availability_id != null) {
                map.set(a.availability_id, a);
            }
        });
        return map;
    }, [availabilities]);

    // 🔍 Single place where we decide if a cleaner mismatches ID requirements
    const isIdMismatch = (slot, booking) => {
        // Try by availability_id first
        let availability = slot.availability_id
            ? availabilityMap.get(slot.availability_id)
            : null;

        // Fallback: try by cleaner_id if needed
        if (!availability && slot.cleaner_id) {
            availability = availabilities.find(
                (a) => a.cleaner_id === slot.cleaner_id
            );
        }

        if (!availability) return false; // unknown → don’t flag

        const hasCompanyId = !!availability.does_have_id_from_company;
        const hasAnyId = hasCompanyId || !!availability.does_have_id;

        const requiresCompanyId = !!booking.is_company_id_required;
        const requiresAnyId =
            requiresCompanyId ||
            !!booking.is_id_required ||
            !!booking.is_accompany_id_required;

        return (
            // Company ID required, but cleaner doesn't have company ID
            (requiresCompanyId && !hasCompanyId) ||
            // Any ID required, but cleaner has no ID at all
            (requiresAnyId && !hasAnyId)
        );
    };

    // ✅ Chip color (uses the same logic)
    const getCleanerChipClass = (slot, booking) => {
        const base = 'scheduling-cleaner-chip assigned';

        if (isIdMismatch(slot, booking)) {
            return `${base} scheduling-cleaner-chip--id-mismatch`;
        }

        return base; // green by default
    };

    // 🔹 Find availability row for a given slot (by availability_id or cleaner_id)
    const getAvailabilityForSlotSimple = (slot) => {
        if (!slot) return null;

        if (slot.availability_id != null) {
            return availabilities.find(
                (a) => a.availability_id === slot.availability_id
            );
        }

        if (slot.cleaner_id != null) {
            return availabilities.find(
                (a) => a.cleaner_id === slot.cleaner_id
            );
        }

        return null;
    };

    // 🔹 Format cleaner label for copy text, e.g. "Jamaica (Trial)" or "Anna (PT, Trial)"
    const formatCleanerLabelForCopy = (slot) => {
        if (!slot?.name) return null;

        const availability = getAvailabilityForSlotSimple(slot);
        let label = slot.name;
        const flags = [];

        if (availability?.is_part_timer) {
            flags.push('PT');
        }
        if (availability?.is_trial) {
            flags.push('Trial');
        }

        if (flags.length > 0) {
            label += ` (${flags.join(', ')})`;
        }

        return label;
    };

    const handleCopyBooking = async (booking, bookingIndex) => {
        try {
            const number = bookingIndex + 1;

            const bedLabel =
                booking.bedrooms === 0
                    ? 'S'
                    : (booking.bedrooms ?? '-');

            const bathLabel =
                booking.bathrooms !== null && booking.bathrooms !== undefined
                    ? booking.bathrooms
                    : '-';

            const firstLine = `${number}. ${booking.client_name} - ${booking.building_name} - Unit ${booking.unit_number} (${bedLabel} - ${bathLabel})`;

            const slots = assignments[booking.id] || [];
            const cleanerNames = slots
                .map((slot) => formatCleanerLabelForCopy(slot))
                .filter(Boolean);

            const cleanersText = cleanerNames.length
                ? cleanerNames.join(', ')
                : 'Not assigned yet';

            const accessRaw =
                booking.property_access || booking.property_access_note || '';
            const accessText =
                accessRaw && String(accessRaw).trim() !== ''
                    ? String(accessRaw).trim()
                    : 'Not Provided';

            const baseType = booking.booking_type || 'Cleaning';
            const showLinenHire = !!booking.is_linen_hire && !!booking.is_laundry;
            const typeWithLinen = showLinenHire
                ? `${baseType} + Linen Hire`
                : baseType;


            const durationText = booking.duration
                ? `${booking.duration} hours`
                : '';
            const typeLine = durationText
                ? `Type: ${typeWithLinen} (${durationText})`
                : `Type: ${typeWithLinen}`;

            const admins = [
                booking.account_manager_first_name,
                booking.account_coordinator_first_name,
            ]
                .map((v) => String(v || '').trim())
                .filter(Boolean);

            const adminsLine = admins.length ? `Admins: ${admins.join(', ')}` : null;

            const lines = [
                firstLine,
                `Cleaners: ${cleanersText}`,
                `Access: ${accessText}`,
                `Start Time: ${booking.start_time}`,
                typeLine,
            ];

            if (booking.has_spot_cleaner) {
                lines.push('Spot Cleaning Machine Required');
            }

            if (shouldShowServiceFlags(booking)) {
                lines.push(
                    `Amenities: ${formatYesNo(booking.is_amenities)}, Laundry: ${formatYesNo(booking.is_laundry)}`
                );
            }

            // ✅ Admins last
            if (adminsLine) {
                lines.push(adminsLine);
            }

            const textToCopy = lines.join('\n');
            await navigator.clipboard.writeText(textToCopy);

            // ✅ show tick for this booking for 2 seconds
            setCopiedBookingId(booking.id);
            setTimeout(() => {
                setCopiedBookingId((prev) =>
                    prev === booking.id ? null : prev
                );
            }, 2000);
        } catch (err) {
            console.error('Failed to copy booking details:', err);
        }
    };

    const getBookingCardClass = (booking) => {
        const base = 'scheduling-booking-card';

        const slots = assignments[booking.id] || [];

        // Required vs assigned
        const rawRequired = booking.required_cleaners ?? 1;
        const required = Math.max(0, Number(rawRequired));
        const assignedCount = slots.length;
        const notEnoughCleaners = assignedCount < required;

        // Permit missing
        const missingPermit = booking.is_permit_needed && !booking.has_permit;

        // Any ID mismatch among assigned cleaners
        const hasIdProblem = slots.some((slot) => isIdMismatch(slot, booking));

        if (notEnoughCleaners || missingPermit || hasIdProblem) {
            return `${base} scheduling-booking-card--warning`;
        }

        return base;
    };



    // Helper: render one booking card (used for both area groups)
    const renderBookingCard = (b, index, { showBathrooms = false, route = null } = {}) => {
        // A booking with no cleaners yet has no stops, so routeByBooking has
        // nothing for it — but it is still rendered inside a route column by
        // area. Show THAT route: there is no such thing as unassigned.
        const effectiveRouteId = routeByBookingEffective[b.id] ?? route?.id ?? null;
        const rawRequired = b.required_cleaners ?? 1;
        const required = Math.max(0, Number(rawRequired));
        const assignedCount = (assignments[b.id] || []).length;
        const hasEnough = assignedCount >= required;
        const bookingNumber = index + 1;

        return (
            <Droppable droppableId={`booking-${b.id}`} direction="horizontal">
                {(provided, snapshot) => (
                    <div
                        className={
                            getBookingCardClass(b) +
                            (snapshot.isDraggingOver
                                ? ' scheduling-booking-card--drag-over'
                                : '')
                        }
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        <div className="scheduling-booking-line">
                            <Link
                                to={`/bookings/${b.id}`}
                                className="scheduling-booking-address"
                                style={{ color: getAreaColor(b.area), textDecoration: 'none' }}
                            >
                                {bookingNumber}. {b.client_name} – {b.building_name} – Unit {b.unit_number} ({b.bedrooms === 0 ? 'S' : b.bedrooms} - {b.bathrooms})
                            </Link>
                        </div>

                        <div className="scheduling-booking-header">
                            <div className="scheduling-booking-main-part">
                                <div className="scheduling-booking-line">
                                    <span className="scheduling-booking-time">
                                        {b.start_time} · {b.duration} hours
                                    </span>
                                </div>
                                <div className="scheduling-booking-line">
                                    <span className="scheduling-booking-type">
                                        {displayBookingType(b.booking_type)}
                                        {b.is_linen_hire && b.is_laundry ? ' + Linen Hire' : ''}
                                    </span>
                                </div>

                                {shouldShowServiceFlags(b) && (
                                    <div className="scheduling-booking-line">
                                        <span className="scheduling-booking-note">
                                            Amenities: {b.is_amenities ? 'Yes' : 'No'} · Laundry: {b.is_laundry ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                )}

                                {renderAccessRequirementBadges(b)}

                                {b.scheduling_note && b.scheduling_note.trim() !== '' && (
                                    <div className="scheduling-booking-line">
                                        <span className="scheduling-booking-note">
                                            Scheduling note: {b.scheduling_note}
                                        </span>
                                    </div>
                                )}

                                {b.has_spot_cleaner && (
                                    <div className="scheduling-booking-line">
                                        <span className="scheduling-booking-note scheduling-booking-note--spot-cleaner">
                                            Spot Cleaner Machine Required
                                        </span>
                                    </div>
                                )}

                                {/* Who is actually where, from the stop ledger — the same rows
                                    the driver's app writes, so this cannot drift from reality. */}
                                {(() => {
                                    const courier = courierStopFor(b.id);
                                    if (courier) {
                                        const done = courier.at
                                            ? new Date(courier.at).toLocaleTimeString('en-GB', {
                                                hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai',
                                            })
                                            : '';
                                        return (
                                            <div className="scheduling-progressRow">
                                                <span className="scheduling-progressChip scheduling-progressChip--collected">
                                                    {`${courier.label}${done ? ` ${done}` : ''}`}
                                                </span>
                                            </div>
                                        );
                                    }

                                    const progress = cleanerProgressFor(b.id);
                                    if (!progress.length) return null;
                                    // Dubai wall-clock, so a drop-off time never reads in the
                                    // viewer's local zone.
                                    const t = (iso) => (iso
                                        ? new Date(iso).toLocaleTimeString('en-GB', {
                                            hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai',
                                        })
                                        : '');
                                    /* Three of these labels are claims about
                                       the clock — she is cleaning (since
                                       when?), the job is done (at what time?),
                                       the driver has taken her (when, and
                                       where to?) — and the answer used to live
                                       only in the tooltip or on the booking
                                       page. It rides in the label itself now
                                       (2026-08-30). The rest stay bare: on a
                                       board this dense, a timestamp has to be
                                       answering a live question to earn the
                                       space. */
                                    const chipText = (c) => {
                                        if (c.state === 'cleaning' && c.workStartedAt) {
                                            return `cleaning · started ${t(c.workStartedAt)}`;
                                        }
                                        if (c.state === 'awaiting_collection' && c.workFinishedAt) {
                                            return `done ${t(c.workFinishedAt)} · waiting for pickup`;
                                        }
                                        if (c.state === 'collected' && c.at) {
                                            return `collected ${t(c.at)} · ${c.whereNow}`;
                                        }
                                        return c.label;
                                    };
                                    return (
                                        <div className="scheduling-progressRow">
                                            {progress.map((c) => (
                                                <span
                                                    key={c.cleaner_id}
                                                    className={'scheduling-progressChip scheduling-progressChip--' + c.state}
                                                    title={c.collected
                                                        ? `${c.name} was collected from here at ${t(c.at)}`
                                                        : c.dropped
                                                        ? `${c.name} was dropped off at ${t(c.at)}`
                                                        : c.picked
                                                            ? `${c.name} was picked up at ${t(c.at)} and is in the car`
                                                            : `${c.name} has not been collected by the driver yet`}
                                                >
                                                    {c.name} · {chipText(c)}
                                                    {/* The DROP-OFF time earns its space in
                                                        the one state where it is still the live
                                                        question: she is at the unit and has not
                                                        started (2026-07-27). Every other state
                                                        that has a time now carries it inside
                                                        chipText, so nothing is repeated here. */}
                                                    {c.state === 'at_unit' && c.at && ` ${t(c.at)}`}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                })()}

                                <div className="scheduling-booking-meta">
                                    <span
                                        className={
                                            'scheduling-req-chip ' +
                                            (hasEnough
                                                ? 'scheduling-req-chip--ok'
                                                : 'scheduling-req-chip--short')
                                        }
                                    >
                                        {assignedCount}/{required} cleaner
                                    </span>
                                    {showBathrooms ? (
                                        <span>
                                            {b.bedrooms === 0 ? 'Studio' : `${b.bedrooms} Bedrooms`} · {b.bathrooms} bath
                                        </span>
                                    ) : (
                                        <span>
                                            {b.bedrooms === 0 ? 'Studio' : `${b.bedrooms} Bedrooms`}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="scheduling-booking-actions">

                                {/* Give this unit to a driver. A menu rather than dragging the
                                    whole card: these cards are tall, the columns scroll
                                    independently, and precision matters more than gesture. */}
                                <div className="scheduling-moveRow">
                                    <button
                                        type="button"
                                        className="scheduling-moveBtn"
                                        onClick={() => setMoveMenuFor(moveMenuFor === b.id ? null : b.id)}
                                        title="Move this booking to another driver's route"
                                    >
                                        <span
                                            className="scheduling-moveDot"
                                            style={{ background: routes.find((r) => r.id === effectiveRouteId)?.route_color || '#cbd5e1' }}
                                        />
                                        {routes.find((r) => r.id === effectiveRouteId)?.route_name || '—'}
                                        <span className="scheduling-moveCaret">▾</span>
                                    </button>

                                    {moveMenuFor === b.id && (
                                        <div className="scheduling-moveMenu">
                                            {routes.map((r) => (
                                                <button
                                                    key={r.id}
                                                    type="button"
                                                    className={'scheduling-moveItem'
                                                        + (effectiveRouteId === r.id ? ' is-current' : '')}
                                                    onClick={() => moveBookingToRoute(b.id, r.id)}
                                                >
                                                    <span className="scheduling-moveDot"
                                                        style={{ background: r.route_color || '#cbd5e1' }} />
                                                    {r.route_name}
                                                    {r.driver_first_name ? ` · ${r.driver_first_name}` : ''}
                                                </button>
                                            ))}

                                        </div>
                                    )}
                                </div>


                                <div className='scheduling-booking-action-buttons'>
                                    <button
                                        type="button"
                                        className={
                                            'scheduling-booking-edit-btn scheduling-booking-copy-btn' +
                                            (copiedBookingId === b.id ? ' is-copied' : '')
                                        }
                                        onClick={() => handleCopyBooking(b, index)}
                                        title={
                                            copiedBookingId === b.id
                                                ? 'Copied!'
                                                : 'Copy this booking details'
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={copiedBookingId === b.id ? faCheck : faCopy}
                                        />
                                    </button>

                                    <button
                                        type="button"
                                        className="scheduling-booking-edit-btn"
                                        onClick={() => setEditBookingId(b.id)}
                                        title="Quick edit booking"
                                    >
                                        <FontAwesomeIcon icon={faPencilAlt} />
                                    </button>
                                </div>

                                {renderBookingBadges(b)}
                            </div>
                        </div>

                        {/* Chips row still lives inside, but any drop inside the card is valid */}
                        <div className="scheduling-cleaner-dropzone">
                            {(assignments[b.id] || []).map((slot, idx) => (
                                <Draggable
                                    key={slot.id}
                                    draggableId={slot.id}
                                    index={idx}
                                >
                                    {(dragProvided) => (
                                        <div
                                            className={getCleanerChipClass(slot, b)}
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                        >
                                            {slot.name}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                            {(!assignments[b.id] || assignments[b.id].length === 0) && (
                                <span className="scheduling-drop-hint">
                                    No cleaner assigned yet, drag cleaners here.
                                </span>
                            )}
                        </div>

                        {/* Admins line */}
                        {(b.account_manager_first_name || b.account_coordinator_first_name) && (
                            <div className="scheduling-booking-line scheduling-booking-admin-line">
                                <span className="scheduling-booking-note scheduling-booking-note--admins">
                                    Admins: {[b.account_manager_first_name, b.account_coordinator_first_name]
                                        .map((v) => String(v || '').trim())
                                        .filter(Boolean)
                                        .join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </Droppable>
        );
    };

    // Compute minimal number of cleaners needed for a list of bookings,
    // taking required_cleaners and overlapping times into account.
    function computeRequiredCleanersForList(bookingsList) {
        const events = [];

        bookingsList.forEach((b) => {
            const start = parseTime(b.start_time);
            const end = parseTime(b.end_time);
            if (isNaN(start) || isNaN(end)) return;

            // null/undefined -> 1, but 0 stays 0
            const rawRequired = b.required_cleaners ?? 1;
            const count = Math.max(0, Number(rawRequired));

            events.push({ time: start, delta: count });
            events.push({ time: end, delta: -count });
        });

        if (events.length === 0) return 0;

        events.sort((a, b) => {
            if (a.time !== b.time) return a.time - b.time;
            return a.delta - b.delta; // process - before + at same time
        });

        let current = 0;
        let max = 0;
        for (const e of events) {
            current += e.delta;
            if (current > max) max = current;
        }
        return max;
    }

    // Check if moving this cleaner from sourceBookingId to destBookingId
    // would cause an overlap with any of their *other* bookings that day.
    const hasBookingConflictForCleaner = (slot, sourceBookingId, destBookingId) => {
        if (!destBookingId) return false;

        const destBooking = bookings.find((b) => b.id === destBookingId);
        if (!destBooking) return false;

        const destStart = parseTime(destBooking.start_time);
        const destEnd = parseTime(destBooking.end_time);
        if (isNaN(destStart) || isNaN(destEnd)) return false;

        const sameCleaner = (otherSlot) => {
            if (slot.availability_id && otherSlot.availability_id) {
                return otherSlot.availability_id === slot.availability_id;
            }
            if (slot.cleaner_id && otherSlot.cleaner_id) {
                return otherSlot.cleaner_id === slot.cleaner_id;
            }
            return false;
        };

        const overlaps = (booking) => {
            const s = parseTime(booking.start_time);
            const e = parseTime(booking.end_time);
            if (isNaN(s) || isNaN(e)) return false;
            // classic time-overlap check
            return s < destEnd && destStart < e;
        };

        // Look at all other bookings this cleaner is on (except the one we are moving from)
        for (const b of bookings) {
            if (b.id === sourceBookingId) continue; // because we are moving them away from this one

            const list = assignments[b.id] || [];
            if (list.some(sameCleaner) && overlaps(b)) {
                return true;
            }
        }

        return false;
    };

    const renderIdBadge = (availability) => {
        if (!availability) return null;

        const { does_have_id, does_have_id_from_company } = availability;

        let text = 'No ID';
        let className = 'scheduling-id-badge scheduling-id-badge--none';

        if (does_have_id_from_company) {
            text = 'Company ID';
            className = 'scheduling-id-badge scheduling-id-badge--company';
        } else if (does_have_id) {
            text = 'Has ID';
            className = 'scheduling-id-badge scheduling-id-badge--has';
        }

        return <span className={className}>{text}</span>;
    };


    const ensureAvailabilities = useCallback(async (date) => {
        try {
            await axios.post('/api/scheduling/cleaners-availability/ensure', { date });
        } catch (err) {
            console.error('Failed to ensure cleaner availability:', err);
            // Non-blocking
        }
    }, []);

    const fetchBookings = useCallback(
        async (date, availabilityList = []) => {
            const res = await axios.get(`/api/scheduling/${date}`);
            const list = res.data.bookings || [];

            // Build map from cleaner_id -> availability row
            const byCleanerId = new Map();
            (availabilityList || []).forEach((a) => {
                if (a.cleaner_id) {
                    byCleanerId.set(a.cleaner_id, a);
                }
            });

            const initialAssignments = {};

            list.forEach((b) => {
                const cleanerIds = Array.isArray(b.cleaner_ids)
                    ? b.cleaner_ids
                    : JSON.parse(b.cleaner_ids || '[]');

                const slots = cleanerIds.map((cleanerId, index) => {
                    const avail = byCleanerId.get(cleanerId) || {};
                    return {
                        id: `b${b.id}-c${cleanerId}-${index}`,
                        availability_id: avail.availability_id || null,
                        cleaner_id: cleanerId,
                        name: avail.name || `Cleaner #${cleanerId}`,
                    };
                });

                initialAssignments[b.id] = slots;
            });

            setBookings(list);
            setAssignments(initialAssignments);
        },
        []
    );

    const fetchPartTimeCleaners = useCallback(async () => {
        try {
            const res = await axios.get('/api/scheduling/part-time-cleaners');
            const rows = res.data.part_time_cleaners || [];

            const options = rows.map((u) => {
                const name = u.first_name;

                return {
                    value: u.id,
                    label: `${name}`,
                    raw: u,
                };
            });

            setPartTimeOptions(options);
        } catch (err) {
            console.error('Failed to load part-time cleaners:', err);
        }
    }, []);


    const fetchAvailabilities = useCallback(async (date) => {
        const res = await axios.get('/api/scheduling/cleaners-availability', {
            params: { date },
        });

        const mapped = (res.data.availabilities || []).map((row) => {
            const fullName = row.first_name + " " + row.last_name;
            const displayName =
                fullName ||
                (row.cleaner_id ? `Cleaner #${row.cleaner_id}` : 'Part-timer');

            return {
                id: row.id,
                availability_id: row.id,
                cleaner_id: row.cleaner_id,
                name: displayName,
                is_part_timer: row.is_part_timer,
                // trial flag comes from staff_profiles (or users behind the scenes)
                is_trial: !!row.staff_is_trial,
                is_available: row.is_available,
                does_have_id: row.does_have_id,
                does_have_id_from_company: row.does_have_id_from_company,
                staff_profile_id: row.staff_profile_id,
                staff_eid: row.staff_eid,
            };
        });

        setAvailabilities(mapped);
        return mapped;          // 🔥 return for use in fetchBookings
    }, []);


    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            await ensureAvailabilities(selectedDate);

            // 1) get availabilities (and keep a local copy)
            const availabilityList = await fetchAvailabilities(selectedDate);

            // 2) now fetch bookings and build assignments using those availabilities
            await fetchBookings(selectedDate, availabilityList);
        } catch (err) {
            console.error('Failed to load scheduling data:', err);
            setError('Failed to load scheduling data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [ensureAvailabilities, fetchAvailabilities, fetchBookings, selectedDate]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        fetchPartTimeCleaners();
    }, [fetchPartTimeCleaners]);

    // ── Driver routes ────────────────────────────────────────────────────────
    // The area blocks on this page were always the driver routes — each one
    // ends in a Copy button whose text gets sent to that driver. They are now
    // real records, so a third driver is an "Add route" click.

    // Distinct, readable colours for routes beyond the standard two.
    const ROUTE_PALETTE = useMemo(
        () => ['#476f37', '#a43777', '#683d1e', '#0f766e', '#7c3aed'], []);

    // Route names describe the patch of Dubai they cover, not which driver has
    // them today — drivers change, the area does not. Suggest one not in use.
    const AREA_SUGGESTIONS = useMemo(
        () => ['JVC', 'Palm Jumeirah', 'Dubai Hills', 'JLT', 'Business Bay',
            'Creek Harbour', 'Meydan', 'JBR'], []);

    const DEFAULT_ROUTE_SEED = useMemo(() => ([
        // Names must match routingService.SEED_AREA_GROUPS or the backend
        // cannot seed the first assignment by area.
        { name: 'Downtown', color: '#4c7ac5' },
        { name: 'Marina', color: '#ae2021' },
    ]), []);

    const loadRouting = useCallback(async (dateStr) => {
        try {
            let list = (await axios.get(`/api/routing/${dateStr}/routes`)).data.routes || [];

            // Downtown and Marina ALWAYS exist — every day starts as the two
            // standard routes, exactly as before, and staff only ever add to
            // that. Any missing one is recreated (not just when the day is
            // completely empty), so a route can never quietly go absent and
            // leave its bookings stranded.
            const missing = DEFAULT_ROUTE_SEED.filter(
                (seed) => !list.some((r) => r.route_name === seed.name)
            );
            if (missing.length) {
                for (const seed of missing) {
                    try {
                        await axios.post(`/api/routing/${dateStr}/routes`, seed);
                    } catch (e) {
                        // 409 just means someone else opened the page first.
                        if (e?.response?.status !== 409) throw e;
                    }
                }
                list = (await axios.get(`/api/routing/${dateStr}/routes`)).data.routes || [];
            }

            // Build the day's stops from the schedule. Idempotent, and it never
            // pulls a booking back off the driver an admin moved it to.
            const gen = (await axios.post(`/api/routing/${dateStr}/generate`)).data || {};

            const stopsRes = (await axios.get(`/api/routing/${dateStr}/stops`)).data || {};
            const stops = stopsRes.stops || [];
            const map = {};
            const byBooking = {};
            stops.forEach((s) => {
                if (s.route_id) map[s.booking_id] = s.route_id;
                (byBooking[s.booking_id] = byBooking[s.booking_id] || []).push(s);
            });
            // A booking parked on a route before it has stops (no cleaner yet,
            // or the day not regenerated) lives on bookings.route_id — the
            // server lists those beside the stops. Stops win when both exist.
            const known = new Set(list.map((r) => r.id));
            (stopsRes.parked || []).forEach((p) => {
                if (map[p.booking_id] == null && p.route_id && known.has(p.route_id)) map[p.booking_id] = p.route_id;
            });
            setStopsByBooking(byBooking);

            setRoutes(list);
            setRouteByBooking(map);
            // A generate that quietly created nothing is exactly what this
            // banner is for. Work stranded on another day (a booking moved
            // after its stops were made) is invisible otherwise — it took a
            // day to notice on booking 20483.
            setRoutingError(gen.warning || '');
            setRoutesLoaded(true);
        } catch (err) {
            console.error('Failed to load routes:', err);
            // Routing is additive: if it fails the page must still schedule
            // cleaners, just without route grouping.
            setRoutingError('Routes could not be loaded — reload the page to try again.');
            setRoutes([]);
            setRoutesLoaded(true);
        }
    }, [DEFAULT_ROUTE_SEED]);

    useEffect(() => {
        setRoutesLoaded(false);
        loadRouting(selectedDate);
    }, [loadRouting, selectedDate]);

    /**
     * LIVE REFRESH. This board is watched all day while drivers tick stops off
     * in the app, so it must not need a page reload to tell the truth (the owner
     * 2026-07-27) — who is in the car, who is at a unit, whether a cleaning has
     * started, and which route a booking sits on.
     *
     * Deliberately NOT loadRouting(): that POSTs /generate first, which is a
     * write and takes ~1.6s against the remote database — far too heavy to run
     * on a timer. This is a plain read of the stops.
     *
     * It also merges ONLY `status` back into the bookings. Re-running
     * fetchBookings would call setAssignments and wipe any cleaner the admin
     * has dragged but not yet saved, which would be much worse than stale data.
     */
    const refreshLive = useCallback(async (dateStr) => {
        try {
            const stopsRes = (await axios.get(`/api/routing/${dateStr}/stops`)).data || {};
            const stops = stopsRes.stops || [];
            const routeMap = {};
            const byBooking = {};
            const statusByBooking = {};
            stops.forEach((s) => {
                if (s.route_id) routeMap[s.booking_id] = s.route_id;
                (byBooking[s.booking_id] = byBooking[s.booking_id] || []).push(s);
                if (s.booking_status) statusByBooking[s.booking_id] = s.booking_status;
            });
            // PARKED BOOKINGS STAY PUT (owner, 2026-09-11: "add many bookings,
            // wait a few seconds, all the bookings I added disappear, reload
            // and they are there again"). This refresh used to rebuild the
            // route map from stops alone, so a booking moved onto a route
            // before its stops existed was thrown back to its area column on
            // the next tick. The server now sends those with the stops.
            (stopsRes.parked || []).forEach((p) => {
                if (routeMap[p.booking_id] == null && p.route_id) routeMap[p.booking_id] = p.route_id;
            });
            setStopsByBooking(byBooking);
            // A route this board does not know (archived, or created by a
            // colleague since the last load) must not swallow a card: keep the
            // mapping only for routes on screen.
            setRoutes((prevRoutes) => {
                const known = new Set(prevRoutes.map((r) => r.id));
                Object.keys(routeMap).forEach((k) => { if (!known.has(routeMap[k])) delete routeMap[k]; });
                return prevRoutes;
            });
            setRouteByBooking(routeMap);
            setBookings((prev) => prev.map((b) => (
                statusByBooking[b.id] && statusByBooking[b.id] !== b.status
                    ? { ...b, status: statusByBooking[b.id] }
                    : b
            )));
        } catch (e) {
            // A poll that fails is not worth a banner — the next one will do.
            console.debug('Live refresh skipped:', e?.message);
        }
    }, []);

    useEffect(() => {
        // Only while the tab is actually being looked at, and immediately on
        // return to it — an admin coming back from WhatsApp wants the truth now,
        // not in fifteen seconds.
        const tick = () => {
            if (document.visibilityState === 'visible') refreshLive(selectedDate);
        };
        const timer = setInterval(tick, 20000);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [refreshLive, selectedDate]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get('/api/team/role/?role=driver');
                setDrivers(res.data?.users || res.data || []);
            } catch (e) { console.error('Failed to load drivers:', e); }
        })();
    }, []);

    const assignDriver = useCallback(async (routeId, driverId) => {
        // Optimistic: the select must feel instant. Reverts on failure.
        //
        // The NAME must move with the id. The <Select> builds its label from
        // route.driver_first_name/driver_last_name, which the server supplies
        // by join — so setting only assigned_driver_id left the old driver's
        // name on screen and made it look like nothing had saved until a full
        // page reload (2026-07-28).
        const picked = drivers.find((d) => d.id === driverId) || null;
        setRoutes((prev) => prev.map((r) => (
            r.id === routeId
                ? {
                    ...r,
                    assigned_driver_id: driverId,
                    driver_first_name: picked ? picked.first_name : null,
                    driver_last_name: picked ? picked.last_name : null,
                }
                : r
        )));
        setRouteBusy(true);
        try {
            await axios.patch(`/api/routing/routes/${routeId}`, { driver_id: driverId });
            // No refetch on success. The name is already set above, and
            // loadRouting POSTs /generate first — a ~1.6s write that would make
            // a one-field change feel like a page load.
        } catch (e) {
            console.error('Failed to assign driver:', e);
            setRoutingError('That driver could not be assigned — reloading.');
            loadRouting(selectedDate);
        } finally {
            setRouteBusy(false);
        }
    }, [loadRouting, selectedDate, drivers]);

    const submitRouteDialog = useCallback(async (payload) => {
        setRouteDialogBusy(true);
        try {
            if (routeDialog?.mode === 'add') {
                await axios.post(`/api/routing/${selectedDate}/routes`, {
                    name: payload.name,
                    claim_area: payload.claimArea || null,
                    // Picked an area? Wear that area's colour — the same map the
                    // booking titles use, so a JVC route reads as JVC everywhere
                    // instead of getting an unrelated palette colour. A typed
                    // name has no area to borrow from, so it takes the palette.
                    color: payload.claimArea
                        ? getAreaColor(payload.claimArea)
                        : ROUTE_PALETTE[routes.length % ROUTE_PALETTE.length],
                });
                setRouteDialog(null);
                await loadRouting(selectedDate);
            } else if (routeDialog?.mode === 'remove') {
                const goneId = routeDialog.route.id;
                await axios.delete(`/api/routing/routes/${goneId}`);
                if (activeRouteId === goneId) setActiveRouteId('all');
                // Drop it from view the moment the server confirms, rather than
                // leaving it on screen while the day is rebuilt. Its bookings
                // fall back to their area column immediately, because
                // bookingsForRoute seeds by area when a stop has no route.
                setRouteDialog(null);
                setRoutes((prev) => prev.filter((r) => r.id !== goneId));
                setRouteByBooking((prev) => {
                    const next = { ...prev };
                    Object.keys(next).forEach((k) => { if (next[k] === goneId) delete next[k]; });
                    return next;
                });
                // The server releases the bookings parked on it; mirror that
                // on the cached rows so nothing keeps pointing at the gone route.
                setBookings((prev) => prev.map((b) => (b.route_id === goneId ? { ...b, route_id: null } : b)));
                // Re-home in the background; the board is already correct.
                loadRouting(selectedDate);
            }
        } catch (e) {
            setRoutingError(e?.response?.data?.error || 'That did not work — please try again.');
            setRouteDialog(null);
        } finally {
            setRouteDialogBusy(false);
        }
    }, [routeDialog, routes.length, selectedDate, activeRouteId, loadRouting, ROUTE_PALETTE]);

    /**
     * Put a booking on another driver's route. Optimistic so the card jumps
     * columns immediately; reverts by reloading if the server refuses.
     *
     * Only PENDING legs move server-side — a booking already dropped off stays
     * credited to the driver who actually drove her.
     */
    /**
     * Which route each booking belongs to.
     *
     * TWO SOURCES, and both are needed. A booking's stops carry the real
     * per-leg route — but stops are generated per (booking, cleaner), so a
     * cleaning with nobody assigned yet HAS none, and used to be unroutable
     * entirely. bookings.route_id records the route it was put on regardless,
     * and generateStops reads that back when the stops are finally created
     * (2026-07-28).
     *
     * Stops win where they exist: they are what the driver actually sees, and
     * an optimistic move writes there first so the card still moves instantly.
     */
    const routeByBookingEffective = useMemo(() => {
        // Only routes ON THIS BOARD count. The bookings list is fetched once
        // per day and its route_id goes stale the moment a route is removed —
        // the card then pointed at a route that no longer existed and fell
        // into "Not on any route" until a reload (2026-09-11).
        const known = new Set(routes.map((r) => r.id));
        const merged = {};
        bookings.forEach((b) => { if (b.route_id && known.has(b.route_id)) merged[b.id] = b.route_id; });
        Object.keys(routeByBooking).forEach((k) => { if (known.has(routeByBooking[k])) merged[k] = routeByBooking[k]; });
        return merged;
    }, [bookings, routeByBooking, routes]);

    const moveBookingToRoute = useCallback(async (bookingId, routeId) => {
        /**
         * ONE hop, not three (2026-07-28).
         *
         * The card used to jump optimistically, then get overwritten when a
         * refetch rebuilt routeByBooking from scratch, then settle. Two causes:
         * loadRouting POSTs /generate first — a ~1.6s WRITE — and the 20s
         * refreshLive poll rewrites the same map.
         *
         * No refetch is needed: the server tells us how many stops moved, so a
         * non-zero answer means the optimistic state is already right. Only the
         * failure paths touch it again, and they revert THIS booking precisely
         * rather than reloading the whole board.
         */
        const previous = routeByBooking[bookingId];
        const revert = () => setRouteByBooking((prev) => {
            const next = { ...prev };
            if (previous == null) delete next[bookingId];
            else next[bookingId] = previous;
            return next;
        });

        setRouteByBooking((prev) => ({ ...prev, [bookingId]: routeId }));
        setMoveMenuFor(null);
        setRouteBusy(true);
        setRoutingError('');
        try {
            const res = await axios.patch(
                `/api/routing/bookings/${bookingId}/route`, { route_id: routeId }
            );
            // A booking's route lives ONLY on its route_stops rows. One whose
            // stops were never generated matches nothing and comes back
            // 200 { moved: 0 } — which used to read as success and then spring
            // back on the next reload with no explanation.
            // `parked` means it had no stops to move — an unstaffed booking —
            // but the route was recorded ON THE BOOKING and its stops will be
            // born there. That is a success, not a failure, so the card stays
            // put and nothing is said (2026-07-28).
            if (!res?.data?.moved && !res?.data?.parked) {
                revert();
                // The server says WHY. The reasons need different actions, and
                // guessing produced a message that was simply wrong for the
                // commonest case.
                const REASONS = {
                    no_cleaner: 'No cleaner is assigned to this booking yet, so it has no '
                        + 'route stops to move. Assign a cleaner first, then move it.',
                    not_generated: 'This booking has no route stops yet. Generate the '
                        + 'day\u2019s stops, then move it.',
                    already_done: 'Every stop on this booking is already done, so there is '
                        + 'nothing left to move.',
                };
                setRoutingError(
                    REASONS[res?.data?.reason]
                    || 'That booking could not be moved to another route.'
                );
            }
        } catch (e) {
            console.error('Failed to move booking:', e);
            revert();
            setRoutingError('That booking could not be moved.');
        } finally {
            setRouteBusy(false);
        }
    }, [routeByBooking]);

    /**
     * Day-wide transit view per cleaner: her latest completed leg anywhere
     * today, and which booking holds her NEXT pending dropoff.
     *
     * A mid-day transfer is recorded as the PREVIOUS booking's `collect` leg,
     * so the next booking's own legs are all still pending while she is
     * already in the car — read per-booking, that printed "Belen · waiting
     * for driver" on the job she was on her way to (2026-08-07).
     */
    const cleanerTransitById = () => {
        const out = {};
        Object.entries(stopsByBooking).forEach(([bid, legs]) => legs.forEach((leg) => {
            if (leg.cleaner_id == null) return;
            const cur = out[leg.cleaner_id] || (out[leg.cleaner_id] = { last: null, nextDrop: null, done: [] });
            if (leg.status === 'done' && leg.completed_at) {
                // Her whole finished day in order. A booking needs to know
                // what happened NEXT after it let her go, which is a different
                // question from where she is right now — read off `last`, a
                // job she left at lunchtime would claim she went home.
                cur.done.push({ type: leg.stop_type, at: leg.completed_at });
                if (!cur.last || new Date(leg.completed_at) > new Date(cur.last.at)) {
                    cur.last = { type: leg.stop_type, at: leg.completed_at };
                }
            } else if (leg.status === 'pending' && leg.stop_type === 'dropoff') {
                if (!cur.nextDrop || leg.seq < cur.nextDrop.seq) {
                    cur.nextDrop = { bookingId: Number(bid), seq: leg.seq };
                }
            }
        }));
        Object.values(out).forEach((c) => c.done.sort((a, b) => new Date(a.at) - new Date(b.at)));
        return out;
    };

    /**
     * When the WORK on each booking actually started and finished.
     *
     * This is the booking's own record — started_at is stamped when it enters
     * In Process (the cleaner's before-cleaning video going up), completed_at
     * when it is marked Completed. It is a different thing from the driver's
     * legs below, which say where a cleaner is, not what the job did.
     *
     * "cleaning" and "done" are claims about exactly these two moments, so the
     * chips that make them carry the times (2026-08-30). Bookings older than
     * the completion trail fall back to their first report upload, which is the
     * same event seen from the other side.
     */
    const workTimesById = (() => {
        const out = {};
        bookings.forEach((b) => {
            out[b.id] = {
                started: b.started_at || b.first_report_at || null,
                finished: b.completed_at || null,
            };
        });
        return out;
    })();

    /**
     * Where each cleaner on this booking currently is, from her legs.
     *
     * The last completed leg decides it: a done dropoff means she is there
     * (and when), a done pickup means she is in the car on her way. Anything
     * else means the driver has not collected her yet.
     */
    const cleanerProgressFor = (bookingId) => {
        const legs = stopsByBooking[bookingId] || [];
        const transit = cleanerTransitById();
        const byCleaner = new Map();
        legs.forEach((leg) => {
            // `home` is a day-edge leg hung off her LAST booking of the day. It
            // says nothing about how THIS booking went, and counting it here
            // made the last booking of the day look like she'd been collected
            // from it when she'd merely gone home.
            if (leg.stop_type === 'home') return;
            // A courier booking has NO cleaner. Running it through here printed
            // the literal chip "Cleaner #null · waiting for driver" on a job
            // that was already delivered (2026-07-27). See courierStopFor.
            if (leg.cleaner_id == null) return;

            const cur = byCleaner.get(leg.cleaner_id) || {
                cleaner_id: leg.cleaner_id,
                name: leg.cleaner_first_name || `Cleaner #${leg.cleaner_id}`,
                picked: false,
                dropped: false,
                collected: false,
                at: null,
                skipped: false,
                bookingStatus: leg.booking_status,
            };
            if (leg.status === 'done') {
                if (leg.stop_type === 'dropoff') { cur.dropped = true; cur.at = leg.completed_at; }
                // The after-job collection is tracked SEPARATELY from the
                // morning pickup. Lumping them together is what made a booking
                // read "waiting for pickup" long after the driver had taken her
                // away to her next one (2026-07-27).
                else if (leg.stop_type === 'collect') { cur.collected = true; cur.at = leg.completed_at; }
                else { cur.picked = true; if (!cur.dropped) cur.at = leg.completed_at; }
            } else if (leg.status === 'skipped') {
                cur.skipped = true;
            }
            byCleaner.set(leg.cleaner_id, cur);
        });

        // The label has to say what she is waiting FOR, so it combines the
        // driver's legs with the booking's own status:
        //   not collected      -> waiting for the driver
        //   collected          -> in the car
        //   delivered, not started -> at the unit, job not begun
        //   delivered, in process  -> cleaning
        //   delivered, completed   -> job done, driver has not collected her
        return [...byCleaner.values()].map((c) => {
            const st = String(c.bookingStatus || '').toLowerCase();
            const inProcess = st.includes('process');
            const complete = st.includes('complete');
            let state = 'awaiting_pickup';
            let label = 'waiting for driver';
            // Where she is NOW, once this booking has let her go. Only the
            // collected state has an answer; everywhere else the label is
            // already the whole story.
            let whereNow = null;
            if (c.skipped && !c.picked && !c.dropped) {
                state = 'skipped'; label = 'skipped';
            } else if (c.collected) {
                // The driver has already taken her away from THIS booking. She
                // is not waiting anywhere — she is on to the next thing, and
                // "the next thing" is a question the ledger can answer: the
                // first leg she finished AFTER this collect (2026-08-30).
                // Before that the chip read "done · collected", which told the
                // office she had left and nothing about where to.
                //
                // The next leg, not her last one of the day: read off the last,
                // a job she left at lunchtime would claim she went home.
                const next = (transit[c.cleaner_id]?.done || [])
                    .find((l) => new Date(l.at) > new Date(c.at));
                whereNow = next?.type === 'home' ? 'dropped home'
                    : next?.type === 'dropoff' ? 'on the next cleaning'
                        : 'in the car';
                state = 'collected'; label = `collected · ${whereNow}`;
            } else if (c.dropped && complete) {
                state = 'awaiting_collection'; label = 'done · waiting for pickup';
            } else if (c.dropped && inProcess) {
                state = 'cleaning'; label = 'cleaning';
            } else if (c.dropped) {
                state = 'at_unit'; label = 'at unit · not started';
            } else if (c.picked) {
                state = 'in_car'; label = 'in the car';
            } else {
                // Nothing done on THIS booking — but if her latest completed
                // leg anywhere today loaded her INTO the car (a pickup, or a
                // collect from the previous job) and this is the very booking
                // she is being driven to, she is not "waiting" — she is on
                // her way (2026-08-07, Belen after a mid-day transfer).
                const tr = transit[c.cleaner_id];
                if (tr?.last && (tr.last.type === 'pickup' || tr.last.type === 'collect')
                    && tr.nextDrop?.bookingId === bookingId) {
                    state = 'in_car'; label = 'in the car';
                    c.picked = true; c.at = tr.last.at;
                }
            }
            const work = workTimesById[bookingId] || {};
            return {
                ...c,
                state,
                label,
                workStartedAt: work.started || null,
                workFinishedAt: work.finished || null,
                whereNow,
            };
        });
    };

    /**
     * A route's stops in the DRIVER'S order — the exact list her app shows.
     *
     * The booking cards above can only ever approximate it: a booking is ONE
     * card here but TWO stops on her route, a drop and a collect that can be
     * hours apart. So rather than distort the board, this is the real thing,
     * shown on demand (2026-07-27).
     *
     * Day-edge legs (`pickup` from accommodation, `home` at the end) are left
     * out for the same reason the app leaves them off the Route tab: they are
     * about people, not places, and belong to the roll-call.
     */
    const driverStopsFor = (routeId) => {
        const out = [];
        Object.values(stopsByBooking).forEach((legs) => {
            legs.forEach((leg) => {
                if (leg.route_id !== routeId) return;
                if (leg.stop_type === 'pickup' || leg.stop_type === 'home') return;
                out.push(leg);
            });
        });
        return out.sort((a, b) => a.seq - b.seq || a.id - b.id);
    };

    /**
     * A courier booking (Delivery / Pickup) in one line: WHEN the driver did it.
     *
     * There is no cleaner to have a state, so there is nothing to say until the
     * job is done — and once it is, the only fact worth the space is the time
     * (2026-07-27). Returns null while it is still outstanding, and the
     * board shows nothing at all.
     */
    const courierStopFor = (bookingId) => {
        const legs = stopsByBooking[bookingId] || [];
        const leg = legs.find((l) => l.cleaner_id == null && l.status === 'done');
        if (!leg) return null;
        return {
            at: leg.completed_at,
            label: leg.stop_type === 'collect' ? 'Picked up' : 'Delivered',
        };
    };

    /**
     * Each cleaner's CURRENT state, across all her bookings today — what the
     * cleaners panel needs. Her most recently completed leg wins; the booking
     * that leg belongs to decides whether she is cleaning, waiting to start,
     * or finished and waiting to be collected.
     */
    const cleanerStateById = (() => {
        const out = {};
        Object.keys(stopsByBooking).forEach((bid) => {
            cleanerProgressFor(Number(bid)).forEach((c) => {
                const prev = out[c.cleaner_id];
                const rank = { awaiting_pickup: 0, skipped: 0, in_car: 1, at_unit: 2, cleaning: 3, awaiting_collection: 4, collected: 5 };
                // Latest completed leg wins; ties break to the further-along state.
                const newer = !prev
                    || (c.at && prev.at && new Date(c.at) > new Date(prev.at))
                    || (c.at && !prev.at)
                    || (!c.at && !prev.at && rank[c.state] > rank[prev.state]);
                if (newer) out[c.cleaner_id] = c;
            });
        });
        return out;
    })();

    /**
     * Panel names are shown in FULL and truncated by CSS at a fixed column
     * width, so every row cuts at exactly the same point and the badges line
     * up. Cutting in JS (first name + two letters) was too aggressive and made
     * rows a ragged length. The full name stays in the title for hover.
     */
    const shortCleanerName = (full) => String(full || '');

    const bookingsOnRoute = (routeId) =>
        Object.values(routeByBooking).filter((id) => id === routeId).length;



    // All "available" cleaner slots based on availability toggle
    const availableCleanerSlots = useMemo(
        () =>
            availabilities
                .filter((a) => a.is_available)
                .map((a) => ({
                    id: `avail-${a.availability_id}`,
                    availability_id: a.availability_id,
                    cleaner_id: a.cleaner_id,
                    name: a.name,
                })),
        [availabilities]
    );

    // All unavailable full-time cleaners (not part-timers)
    const unavailableFullTimeCleaners = useMemo(
        () =>
            availabilities.filter(
                (a) =>
                    !a.is_available &&
                    a.cleaner_id &&          // real user
                    !a.is_part_timer         // full-time
            ),
        [availabilities]
    );

    // Which availability_ids are already used in assignments (so we don't show them in Unassigned)
    const assignedAvailabilityIds = useMemo(() => {
        const set = new Set();
        Object.values(assignments).forEach((list = []) => {
            list.forEach((slot) => {
                if (slot.availability_id) set.add(slot.availability_id);
            });
        });
        return set;
    }, [assignments]);

    // Total available & how many of them are already assigned at least once
    const totalAvailableCount = useMemo(
        () => availabilities.filter((a) => a.is_available).length,
        [availabilities]
    );

    const unassignedCleanerSlots = useMemo(
        () =>
            availableCleanerSlots.filter(
                (slot) => !assignedAvailabilityIds.has(slot.availability_id)
            ),
        [availableCleanerSlots, assignedAvailabilityIds]
    );

    // 🔹 Helpers: assigned cleaners per area group
    const assignedCleanersGroup1 = useMemo(() => {
        const map = new Map();

        bookingsGroup1.forEach((b) => {
            const endNumeric = parseTime(b.end_time);
            const endIsValid = !isNaN(endNumeric);
            const slots = assignments[b.id] || [];

            slots.forEach((slot) => {
                const key = slot.availability_id || `${slot.cleaner_id}-${slot.name}`;
                const existing = map.get(key);

                const availability = availabilities.find(
                    (a) => a.availability_id === slot.availability_id
                );
                const isPartTimer = availability?.is_part_timer ?? false;

                if (!existing) {
                    map.set(key, {
                        ...slot,
                        latestEnd: endIsValid ? endNumeric : null,
                        latestEndLabel: endIsValid ? b.end_time : null,
                        is_part_timer: isPartTimer,
                    });
                } else if (endIsValid) {
                    if (existing.latestEnd == null || endNumeric > existing.latestEnd) {
                        existing.latestEnd = endNumeric;
                        existing.latestEndLabel = b.end_time;
                    }
                    // is_part_timer is fixed per cleaner, so no need to update it
                }
            });
        });

        const result = Array.from(map.values());

        result.sort((a, b) => {
            const aPT = !!a.is_part_timer;
            const bPT = !!b.is_part_timer;

            // Full-timers first, then part-timers
            if (aPT && !bPT) return 1;
            if (!aPT && bPT) return -1;

            // Within each group, still sort by earliest latestEnd
            if (a.latestEnd == null && b.latestEnd == null) return 0;
            if (a.latestEnd == null) return 1;
            if (b.latestEnd == null) return -1;
            return a.latestEnd - b.latestEnd;
        });

        return result;
    }, [assignments, bookingsGroup1, parseTime, availabilities]);


    const assignedCleanersGroup2 = useMemo(() => {
        const map = new Map();

        bookingsGroup2.forEach((b) => {
            const endNumeric = parseTime(b.end_time);
            const endIsValid = !isNaN(endNumeric);
            const slots = assignments[b.id] || [];

            slots.forEach((slot) => {
                const key = slot.availability_id || `${slot.cleaner_id}-${slot.name}`;
                const existing = map.get(key);

                const availability = availabilities.find(
                    (a) => a.availability_id === slot.availability_id
                );
                const isPartTimer = availability?.is_part_timer ?? false;

                if (!existing) {
                    map.set(key, {
                        ...slot,
                        latestEnd: endIsValid ? endNumeric : null,
                        latestEndLabel: endIsValid ? b.end_time : null,
                        is_part_timer: isPartTimer,
                    });
                } else if (endIsValid) {
                    if (existing.latestEnd == null || endNumeric > existing.latestEnd) {
                        existing.latestEnd = endNumeric;
                        existing.latestEndLabel = b.end_time;
                    }
                    // is_part_timer is fixed per cleaner
                }
            });
        });

        const result = Array.from(map.values());

        result.sort((a, b) => {
            const aPT = !!a.is_part_timer;
            const bPT = !!b.is_part_timer;

            if (aPT && !bPT) return 1;
            if (!aPT && bPT) return -1;

            if (a.latestEnd == null && b.latestEnd == null) return 0;
            if (a.latestEnd == null) return 1;
            if (b.latestEnd == null) return -1;
            return a.latestEnd - b.latestEnd;
        });

        return result;
    }, [assignments, bookingsGroup2, parseTime, availabilities]);

    // Cleaners in each area whose latest booking ends before 5pm
    const availableBefore5Group1 = assignedCleanersGroup1.filter(
        (slot) => slot.latestEnd != null && slot.latestEnd < 17
    ).length;

    const availableBefore5Group2 = assignedCleanersGroup2.filter(
        (slot) => slot.latestEnd != null && slot.latestEnd < 17
    ).length;


    const filteredUnassignedCleanerSlots = useMemo(() => {
        if (!cleanerSearch.trim()) return unassignedCleanerSlots;
        const q = cleanerSearch.toLowerCase();
        return unassignedCleanerSlots.filter((slot) =>
            slot.name.toLowerCase().includes(q)
        );
    }, [unassignedCleanerSlots, cleanerSearch]);

    // Sort available cleaners by ID status: Company ID → Has ID → No ID
    const sortedUnassignedCleanerSlots = useMemo(() => {
        const companyIdFull = [];
        const hasIdFull = [];
        const noIdFull = [];

        const companyIdPT = [];
        const hasIdPT = [];
        const noIdPT = [];

        filteredUnassignedCleanerSlots.forEach((slot) => {
            const availability = availabilities.find(
                (a) => a.availability_id === slot.availability_id
            );
            const isPartTimer = availability?.is_part_timer;

            if (availability?.does_have_id_from_company) {
                if (isPartTimer) {
                    companyIdPT.push(slot);
                } else {
                    companyIdFull.push(slot);
                }
            } else if (availability?.does_have_id) {
                if (isPartTimer) {
                    hasIdPT.push(slot);
                } else {
                    hasIdFull.push(slot);
                }
            } else {
                if (isPartTimer) {
                    noIdPT.push(slot);
                } else {
                    noIdFull.push(slot);
                }
            }
        });

        // Full-timers first (by ID type), then all part-timers
        return [
            ...companyIdFull,
            ...hasIdFull,
            ...noIdFull,
            ...companyIdPT,
            ...hasIdPT,
            ...noIdPT,
        ];
    }, [filteredUnassignedCleanerSlots, availabilities]);

    const filteredAssignedCleanersGroup1 = useMemo(() => {
        if (!cleanerSearch.trim()) return assignedCleanersGroup1;
        const q = cleanerSearch.toLowerCase();
        return assignedCleanersGroup1.filter((slot) =>
            slot.name.toLowerCase().includes(q)
        );
    }, [assignedCleanersGroup1, cleanerSearch]);

    const filteredAssignedCleanersGroup2 = useMemo(() => {
        if (!cleanerSearch.trim()) return assignedCleanersGroup2;
        const q = cleanerSearch.toLowerCase();
        return assignedCleanersGroup2.filter((slot) =>
            slot.name.toLowerCase().includes(q)
        );
    }, [assignedCleanersGroup2, cleanerSearch]);

    const totalAssignedCleanersCount =
        assignedCleanersGroup1.length + assignedCleanersGroup2.length;


    const filteredUnavailableFullTimeCleaners = useMemo(() => {
        if (!cleanerSearch.trim()) return unavailableFullTimeCleaners;
        const q = cleanerSearch.toLowerCase();
        return unavailableFullTimeCleaners.filter((a) =>
            a.name.toLowerCase().includes(q)
        );
    }, [unavailableFullTimeCleaners, cleanerSearch]);

    // Total ladies needed with ID vs Company ID (based on booking access rules)
    const { withIdRequired, withCompanyIdRequired } = useMemo(() => {
        let withIdRequired = 0;
        let withCompanyIdRequired = 0;

        bookings.forEach((b) => {
            // required_cleaners: null/undefined -> 1, 0 stays 0
            const rawRequired = b.required_cleaners ?? 1;
            const required = Math.max(0, Number(rawRequired));

            if (required === 0) return;

            if (b.is_company_id_required) {
                // Company ID is the stricter one → goes into Company ID bucket
                withCompanyIdRequired += required;
            } else if (b.is_id_required || b.is_accompany_id_required) {
                // Any other ID requirement → normal ID bucket
                withIdRequired += required;
            }
        });

        return { withIdRequired, withCompanyIdRequired };
    }, [bookings]);

    const handleToggleAvailability = useCallback(
        async (availabilityId, current) => {
            try {
                setAvailabilities((prev) =>
                    prev.map((a) =>
                        a.availability_id === availabilityId
                            ? { ...a, is_available: !current }
                            : a
                    )
                );

                await axios.patch(
                    `/api/scheduling/cleaners-availability/${availabilityId}`,
                    { is_available: !current }
                );

            } catch (err) {
                console.error('Failed to toggle availability:', err);
                // revert on error
                setAvailabilities((prev) =>
                    prev.map((a) =>
                        a.availability_id === availabilityId
                            ? { ...a, is_available: current }
                            : a
                    )
                );
            }
        },
        []
    );

    const handleCreatePartTimer = useCallback(
        async (e) => {
            e.preventDefault();
            if (!selectedPartTimer) return;

            try {
                setIsCreatingPartTimer(true);

                const isNewOption = !selectedPartTimer.raw; // new = no raw
                let payload;

                if (isNewOption) {
                    // ✅ Create brand-new part-timer using first_name
                    payload = {
                        date: selectedDate,
                        first_name: selectedPartTimer.label,
                        // defaults – tweak as you like
                        does_have_id: false,
                        does_have_id_from_company: false,
                        is_trial: false,
                    };
                } else {
                    // ✅ Existing part-time user from dropdown
                    payload = {
                        date: selectedDate,
                        user_id: selectedPartTimer.value,
                    };

                    // pass through staff-profile flags if available
                    const raw = selectedPartTimer.raw;
                    if (raw) {
                        if (typeof raw.does_have_id === 'boolean') {
                            payload.does_have_id = raw.does_have_id;
                        }
                        if (typeof raw.does_have_id_from_company === 'boolean') {
                            payload.does_have_id_from_company = raw.does_have_id_from_company;
                        }
                        if (typeof raw.is_trial === 'boolean') {
                            payload.is_trial = raw.is_trial;
                        }
                    }
                }

                const res = await axios.post(
                    '/api/scheduling/part-time-cleaners/add-or-ensure',
                    payload
                );

                // If a NEW user was created, add them into the dropdown options
                if (isNewOption && res.data?.user) {
                    const u = res.data.user;
                    const staff = res.data.staff_profile || {};

                    const newOption = {
                        value: u.id,
                        label: u.first_name,
                        raw: {
                            ...staff,
                            first_name: u.first_name,
                        },
                    };

                    setPartTimeOptions((prev) => [...prev, newOption]);
                    setSelectedPartTimer(newOption);
                } else {
                    // Existing user – just clear selection
                    setSelectedPartTimer(null);
                }

                // Refresh scheduling so the new availability appears in the list
                await loadData();
                await fetchPartTimeCleaners();
            } catch (err) {
                console.error('Failed to create/ensure part-timer:', err);
            } finally {
                setIsCreatingPartTimer(false);
            }
        },
        [selectedPartTimer, selectedDate, loadData, fetchPartTimeCleaners]
    );

    const handleDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination) return;

        const sourceId = source.droppableId;
        const destId = destination.droppableId;

        if (sourceId === destId && source.index === destination.index) {
            return;
        }

        // Helper: get required limit for a booking
        const getRequiredForBooking = (bookingId) => {
            const booking = bookings.find((b) => b.id === bookingId);
            const rawRequired = booking?.required_cleaners ?? 1;
            return Math.max(0, Number(rawRequired));
        };

        // Clone current assignments
        const newAssignments = Object.fromEntries(
            Object.entries(assignments).map(([k, v]) => [k, [...(v || [])]])
        );

        // 1) From "Already assigned cleaners" pool → booking (separate by area)
        if (
            sourceId === 'assigned-pool-group1' ||
            sourceId === 'assigned-pool-group2'
        ) {
            if (!destId.startsWith('booking-')) return;

            const destBookingId = Number(destId.replace('booking-', ''));
            const isGroup1Pool = sourceId === 'assigned-pool-group1';

            const filteredPool = isGroup1Pool
                ? filteredAssignedCleanersGroup1
                : filteredAssignedCleanersGroup2;
            const fullPool = isGroup1Pool
                ? assignedCleanersGroup1
                : assignedCleanersGroup2;

            const poolSlot =
                filteredPool[source.index] || fullPool[source.index];

            if (!poolSlot) return;

            const destList = newAssignments[destBookingId] || [];
            const required = getRequiredForBooking(destBookingId);

            // 🔒 Do not exceed required_cleaners
            if (destList.length >= required) {
                return;
            }

            const hasConflict = hasBookingConflictForCleaner(
                poolSlot,
                null,
                destBookingId
            );
            if (hasConflict) {
                return;
            }

            // Avoid adding same cleaner twice to same booking
            const alreadyOnBooking = destList.some((slot) => {
                if (poolSlot.availability_id && slot.availability_id) {
                    return slot.availability_id === poolSlot.availability_id;
                }
                if (poolSlot.cleaner_id && slot.cleaner_id) {
                    return slot.cleaner_id === poolSlot.cleaner_id;
                }
                return false;
            });
            if (alreadyOnBooking) return;

            const newSlot = {
                id: `assign-${destBookingId}-${poolSlot.availability_id || poolSlot.cleaner_id
                    }-${Date.now()}`,
                availability_id: poolSlot.availability_id,
                cleaner_id: poolSlot.cleaner_id,
                name: poolSlot.name,
            };

            destList.splice(destination.index, 0, newSlot);
            newAssignments[destBookingId] = destList;

            setAssignments(newAssignments);
            await assignCleanerToBooking(destBookingId, poolSlot.cleaner_id);
            return;
        }

        // 2) From "unassigned" list → booking
        if (sourceId === 'unassigned') {
            if (!destId.startsWith('booking-')) return;

            // ⬇️ use the *sorted* list that matches the UI
            const draggedSlot = sortedUnassignedCleanerSlots[source.index];
            if (!draggedSlot) return;

            const bookingId = Number(destId.replace('booking-', ''));
            const bookingList = newAssignments[bookingId] || [];
            const required = getRequiredForBooking(bookingId);

            // 🔒 Do not exceed required_cleaners
            if (bookingList.length >= required) {
                return;
            }

            bookingList.splice(destination.index, 0, draggedSlot);
            newAssignments[bookingId] = bookingList;

            setAssignments(newAssignments);
            await assignCleanerToBooking(bookingId, draggedSlot.cleaner_id);
            return;
        }

        // 3) Booking → ...
        if (!sourceId.startsWith('booking-')) return;

        const sourceBookingId = Number(sourceId.replace('booking-', ''));
        const sourceList = newAssignments[sourceBookingId] || [];
        const moved = sourceList[source.index];
        if (!moved) return;

        // ✅ Booking → unassigned OR Booking → assigned-pool
        // Both mean: remove from this specific booking.
        if (destId === 'unassigned' || destId === 'assigned-pool-group1' || destId === 'assigned-pool-group2') {
            sourceList.splice(source.index, 1);
            newAssignments[sourceBookingId] = sourceList;

            setAssignments(newAssignments);
            await removeCleanerFromBooking(sourceBookingId, moved.cleaner_id);
            return;
        }

        // 4) Booking → booking
        if (!destId.startsWith('booking-')) return;

        const destBookingId = Number(destId.replace('booking-', ''));
        const destList = newAssignments[destBookingId] || [];
        const requiredDest = getRequiredForBooking(destBookingId);

        // Reordering inside same booking
        if (sourceBookingId === destBookingId) {
            // Reordering doesn't change count, so no need to check required limit
            sourceList.splice(source.index, 1);
            sourceList.splice(destination.index, 0, moved);
            newAssignments[sourceBookingId] = sourceList;

            setAssignments(newAssignments);
            // no API call needed – order is only visual for now
            return;
        }

        // Moving from one booking to another – first check max cleaners
        // 🔒 Do not exceed required_cleaners on destination booking
        if (destList.length >= requiredDest) {
            return;
        }

        // Avoid duplicates on dest booking
        const alreadyOnDest = destList.some((slot) => {
            if (moved.availability_id && slot.availability_id) {
                return slot.availability_id === moved.availability_id;
            }
            if (moved.cleaner_id && slot.cleaner_id) {
                return slot.cleaner_id === moved.cleaner_id;
            }
            return false;
        });
        if (alreadyOnDest) {
            return;
        }

        // Then check time conflicts
        const hasConflict = hasBookingConflictForCleaner(
            moved,
            sourceBookingId,
            destBookingId
        );
        if (hasConflict) {
            return;
        }

        // Update local state
        sourceList.splice(source.index, 1);
        newAssignments[sourceBookingId] = sourceList;

        destList.splice(destination.index, 0, moved);
        newAssignments[destBookingId] = destList;

        setAssignments(newAssignments);

        // Update backend: remove from old booking, add to new booking
        await Promise.all([
            removeCleanerFromBooking(sourceBookingId, moved.cleaner_id),
            assignCleanerToBooking(destBookingId, moved.cleaner_id),
        ]);
    };

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);

        // Only push to URL if this page is using the route param (i.e. not in a customDate embed)
        if (!customDate) {
            navigate(`/bookings/scheduling/${newDate}`); // adjust base path if needed
        }
    };

    // Asia/Dubai render (item 8) — same "Saturday 19 July 2026" visual.
    const prettyDate = useMemo(
        () => formatDubai(selectedDate, 'cccc d LLLL yyyy'),
        [selectedDate]
    );

    const filterByTimeGroup = (list, group) => {
        if (group === 'morning') {
            return list.filter((b) => parseTime(b.start_time) < 10);
        }
        if (group === 'peak') {
            return list.filter(
                (b) => parseTime(b.start_time) >= 10 && parseTime(b.start_time) < 14
            );
        }
        if (group === 'afternoon') {
            return list.filter((b) => parseTime(b.start_time) >= 14);
        }
        return list;
    };

    const assignCleanerToBooking = useCallback(async (bookingId, cleanerId) => {
        if (!cleanerId) return;

        try {
            await axios.post(`/api/scheduling/assign/${bookingId}/cleaner`, {
                cleaner_id: cleanerId,
            });
        } catch (err) {
            console.error('Failed to assign cleaner to booking:', err);
        }
    }, []);

    const removeCleanerFromBooking = useCallback(async (bookingId, cleanerId) => {
        if (!cleanerId) return;

        try {
            await axios.delete(
                `/api/scheduling/remove/${bookingId}/cleaner/${cleanerId}`
            );
        } catch (err) {
            console.error('Failed to remove cleaner from booking:', err);
        }
    }, []);

    const getBookingsInUiOrder = (bookingsForArea, activeTimeGroup) => {
        // Same order as the UI:
        // - if "all": Morning → Peak → Afternoon
        // - if specific: just that one group
        const groups =
            activeTimeGroup === 'all'
                ? ['morning', 'peak', 'afternoon']
                : [activeTimeGroup];

        const ordered = [];
        groups.forEach((groupKey) => {
            const rawList = filterByTimeGroup(bookingsForArea, groupKey);
            const sorted = sortBookingsByAreaAndTime(rawList);
            ordered.push(...sorted);
        });
        return orderByDriverSequence(ordered);
    };

    /**
     * Put a list in the order the DRIVER will actually visit it.
     *
     * The whole point of phase 2 was that the copy text sent to a driver says
     * the same thing her app does. Once the app started ordering stops by
     * phase and area, this page — still sorting by clock time — drifted away
     * from it (2026-07-27).
     *
     * A booking is ranked by its EARLIEST stop's seq. That is an approximation
     * by necessity: this page is booking-shaped and the route is stop-shaped,
     * so a booking whose cleaner is dropped early and collected late collapses
     * to its first visit. It gets the sequence of ARRIVALS right, which is what
     * the copy text is for. Bookings with no stops yet keep the clock order
     * they already had, at the end.
     */
    const orderByDriverSequence = (list) => {
        const firstSeq = new Map();
        Object.values(stopsByBooking).forEach((legs) => {
            legs.forEach((leg) => {
                const cur = firstSeq.get(leg.booking_id);
                if (cur === undefined || leg.seq < cur) firstSeq.set(leg.booking_id, leg.seq);
            });
        });
        if (firstSeq.size === 0) return list;

        return list
            .map((b, i) => ({ b, i, seq: firstSeq.get(b.id) }))
            .sort((x, y) => {
                if (x.seq === undefined && y.seq === undefined) return x.i - y.i;
                if (x.seq === undefined) return 1;
                if (y.seq === undefined) return -1;
                return x.seq - y.seq || x.i - y.i;
            })
            .map((e) => e.b);
    };

    // 🔹 Spot cleaner flags per area (whole day for that area)

    /**
     * Which bookings belong to which route.
     *
     * Truth is route_stops (routeByBooking), so what an admin drags here is
     * what the driver sees in the app. A booking with no stops yet — nobody
     * assigned to it — falls back to its area seed so the page never looks
     * empty while the day is still being staffed.
     */
    const bookingsForRoute = (route) => {
        const isDowntown = route.route_name === 'Downtown';
        const seedAreas = isDowntown ? group1Locations
            : route.route_name === 'Marina' ? group2Locations
                : null;
        return schedulableBookings.filter((b) => {
            const assigned = routeByBookingEffective[b.id];
            if (assigned) return assigned === route.id;
            if (!seedAreas) return false;
            if (seedAreas.includes(b.area)) return true;
            // Unknown or blank area falls to Downtown, mirroring the server's
            // seed. Nothing is ever left with no route to belong to.
            return isDowntown
                && !group1Locations.includes(b.area)
                && !group2Locations.includes(b.area);
        });
    };

    // Time groups to show based on activeTimeGroup
    const timeGroupsToShow =
        activeTimeGroup === 'all'
            ? ['morning', 'peak', 'afternoon']
            : [activeTimeGroup];

    const timeGroupLabels = {
        morning: 'Morning',
        peak: 'Peak hours',
        afternoon: 'Afternoon',
    };

    const getTimeGroupSummary = (bookingsForArea) => {
        const keys = ['morning', 'peak', 'afternoon'];
        return keys.reduce((acc, key) => {
            const subset = filterByTimeGroup(bookingsForArea, key);
            if (subset.length === 0) return acc;
            const required = computeRequiredCleanersForList(subset);
            acc.push({ key, label: timeGroupLabels[key], required });
            return acc;
        }, []);
    };

    // Every route section the page renders, with everything it needs.
    const routeSections = routes.map((route) => {
        const list = bookingsForRoute(route);
        const summary = getTimeGroupSummary(list);
        return {
            route,
            bookings: list,
            summary,
            maxRequired: summary.reduce((max, s) => Math.max(max, s.required), 0),
            copyList: getBookingsInUiOrder(list, activeTimeGroup),
            hasSpotCleaner: list.some((b) => b.has_spot_cleaner),
        };
    });

    // Bookings no route claimed — visible so work can never be silently lost.
    const orphanBookings = schedulableBookings.filter(
        (b) => !routeSections.some((s) => s.bookings.some((x) => x.id === b.id))
    );

    // Peak concurrent cleaner demand, summed across every route. Previously
    // this was maxGroup1 + maxGroup2; with N routes it is just a sum, and the
    // per-route figure comes from routeSections so a hidden route cannot
    // silently drop out of the headline.
    const visibleSections = routeSections.filter(
        (s) => activeRouteId === 'all' || s.route.id === activeRouteId
    );
    const totalRequiredAll = visibleSections.reduce((sum, s) => sum + s.maxRequired, 0);


    const currentTimeGroupLabel =
        activeTimeGroup === 'all'
            ? 'All day'
            : timeGroupLabels[activeTimeGroup];


    return (
        <div className="scheduling-container">
            <div className="scheduling-header">
                <div>
                    <h1 className="scheduling-title">Cleaner Scheduling</h1>
                    <p className="scheduling-subtitle">
                        Quickly assign available cleaners to today&apos;s bookings with drag &amp; drop.
                    </p>
                </div>
                <div className="scheduling-date-box">
                    <label className="scheduling-date-label">Date</label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        className="scheduling-date-input"
                    />
                    <div className="scheduling-date-pretty">{prettyDate}</div>
                </div>
            </div>

            {/* Route selector — one per driver, however many there are */}
            <div className="scheduling-area-filter">
                <button
                    type="button"
                    onClick={() => setActiveRouteId('all')}
                    className={
                        'scheduling-time-filter-button' +
                        (activeRouteId === 'all' ? ' is-active' : '')
                    }
                >
                    All routes
                </button>
                {routes.map((r) => (
                    <button
                        key={r.id}
                        type="button"
                        onClick={() => setActiveRouteId(r.id)}
                        className={
                            'scheduling-time-filter-button' +
                            (activeRouteId === r.id ? ' is-active' : '')
                        }
                    >
                        {r.route_name}
                        {r.driver_first_name ? ` · ${r.driver_first_name}` : ''}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => setRouteDialog({ mode: 'add' })}
                    className="scheduling-time-filter-button scheduling-toggle-panel-button"
                    title="Add another driver's route for this day"
                >
                    + Add route
                </button>
            </div>

            {/* A moving bar, not a line of grey text: adding a route finishes in
                about a second and a static "Loading…" was easy to miss entirely,
                which read as nothing happening. */}
            {(loading || !routesLoaded || routeDialogBusy || routeBusy) && (
                <div className="scheduling-loadingBar" role="status" aria-label="Loading">
                    <div className="scheduling-loadingBar__fill" />
                </div>
            )}

            {/* A notice nobody can scroll past. Pinned like the loading bar,
                because the inline version sat at the top of a very long board
                and was invisible exactly when it mattered (2026-07-28).
                Click to dismiss — it does not time out, since these say why
                something did NOT happen and want reading. */}
            {routingError && (
                <div
                    className="scheduling-error-banner"
                    role="alert"
                    onClick={() => setRoutingError('')}
                    title="Dismiss"
                >
                    <span>{routingError}</span>
                    <span className="scheduling-error-banner__x" aria-hidden="true">×</span>
                </div>
            )}

            {/* Time window selector */}
            <div className="scheduling-time-filter">
                {[
                    { key: 'morning', label: 'Morning' },
                    { key: 'peak', label: 'Peak hours' },
                    { key: 'afternoon', label: 'Afternoon' },
                    { key: 'all', label: 'Show all' },
                ].map((opt) => (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => setActiveTimeGroup(opt.key)}
                        className={
                            'scheduling-time-filter-button' +
                            (activeTimeGroup === opt.key ? ' is-active' : '')
                        }
                    >
                        {opt.label}
                    </button>
                ))}

                {/* New: toggle cleaners panel */}
                <button
                    type="button"
                    onClick={() => setShowRightPanel((prev) => !prev)}
                    className="scheduling-time-filter-button scheduling-toggle-panel-button"
                >
                    {showRightPanel ? 'Hide Cleaners Panel' : 'Show Cleaners Panel'}
                </button>
            </div>


            {error && <div className="scheduling-error-banner">{error}</div>}

            <DragDropContext onDragEnd={handleDragEnd}>
                <div
                    className={
                        'scheduling-layout' +
                        (showRightPanel ? '' : ' scheduling-layout--single')
                    }
                >
                    {/* Left: bookings grouped by area/time */}
                    <div className="scheduling-column scheduling-column-left">
                        <section className="scheduling-card">
                            <div className="scheduling-card-header scheduling-card-header--bookings">
                                <div>
                                    <h2 className="scheduling-card-title">Bookings overview</h2>
                                    <p className="scheduling-card-subtitle">
                                        Drag cleaners into each booking line. Order of cleaners is saved per booking.
                                    </p>
                                </div>
                                <div className="scheduling-total-required">
                                    <div className="scheduling-total-required-value">
                                        {totalRequiredAll} Cleaners
                                    </div>
                                    <div className="scheduling-total-required-breakdown">
                                        {visibleSections.map((s) => `${s.route.route_name}: ${s.maxRequired}`).join(' · ') || 'No routes yet'}
                                    </div>
                                    <div className="scheduling-total-required-breakdown">
                                        ID needed: {withIdRequired} · Company ID needed: {withCompanyIdRequired}
                                    </div>
                                </div>

                            </div>

                            <div className={'scheduling-bookings-wrapper' + (routeSections.length >= 4 ? ' scheduling-bookings-wrapper--two-up' : '')}>
                                {routesLoaded && routeSections
                                    .filter((s) => activeRouteId === 'all' || s.route.id === activeRouteId)
                                    .map(({ route, bookings: routeBookings, summary, copyList, hasSpotCleaner }) => (
                                        <div className="scheduling-bookings-group" key={route.id}>
                                            <div className="scheduling-group-header-with-copy">
                                                <h3
                                                    className="scheduling-group-title"
                                                    style={route.route_color ? { color: route.route_color } : undefined}
                                                >
                                                    {route.route_name}
                                                </h3>

                                                {/* The driver this route belongs to. Assigning here is
                                                    what makes the copied text and the driver's app agree. */}
                                                <Select
                                                    className="scheduling-route-driver"
                                                    classNamePrefix="schedRouteDriver"
                                                    placeholder="Assign driver"
                                                    isClearable
                                                    options={drivers.map((d) => ({
                                                        value: d.id,
                                                        label: [d.first_name, d.last_name].filter(Boolean).join(' '),
                                                    }))}
                                                    value={route.assigned_driver_id ? {
                                                        value: route.assigned_driver_id,
                                                        label: [route.driver_first_name, route.driver_last_name]
                                                            .filter(Boolean).join(' '),
                                                    } : null}
                                                    onChange={(opt) => assignDriver(route.id, opt ? opt.value : null)}
                                                />

                                                <button
                                                    type="button"
                                                    className="scheduling-driver-view-toggle"
                                                    onClick={() => setDriverViewRoutes((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(route.id)) next.delete(route.id);
                                                        else next.add(route.id);
                                                        return next;
                                                    })}
                                                    title="Show the exact stop list the driver sees"
                                                >
                                                    {driverViewRoutes.has(route.id) ? 'Bookings' : "Driver's order"}
                                                </button>

                                                <ScheduleCopyButton
                                                    selectedDate={selectedDate}
                                                    areaLabel={route.route_name}
                                                    timeGroupLabel={currentTimeGroupLabel}
                                                    bookings={copyList}
                                                    assignments={assignments}
                                                    activeTimeGroup={activeTimeGroup}
                                                    availabilities={availabilities}
                                                    hasSpotCleanerForArea={hasSpotCleaner}
                                                    driverName={[route.driver_first_name, route.driver_last_name]
                                                        .filter(Boolean).join(' ').trim() || null}
                                                />

                                                {/* Downtown and Marina are the standing pair and are
                                                    recreated every day, so removing them would only
                                                    strand their bookings until the next reload. Extra
                                                    routes are the ones you add and drop. */}
                                                {!DEFAULT_ROUTE_SEED.some((d) => d.name === route.route_name) && (
                                                    <button
                                                        type="button"
                                                        className="scheduling-route-remove"
                                                        onClick={() => setRouteDialog({ mode: 'remove', route })}
                                                        title="Remove this route"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>

                                            {summary.length > 0 && (
                                                <div className="scheduling-group-summary">
                                                    {summary.map((s) => (
                                                        <span
                                                            key={s.key}
                                                            className="scheduling-summary-chip"
                                                            style={route.route_color
                                                                ? { borderColor: route.route_color, color: route.route_color }
                                                                : undefined}
                                                        >
                                                            {s.label}: {s.required} cleaner
                                                            {s.required !== 1 ? 's' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {driverViewRoutes.has(route.id) ? (
                                                <ol className="scheduling-driver-stops">
                                                    {driverStopsFor(route.id).map((leg, i) => {
                                                        const who = leg.cleaner_id
                                                            ? (leg.cleaner_first_name || `Cleaner #${leg.cleaner_id}`)
                                                            : displayBookingType(leg.booking_type || 'Delivery');
                                                        const verb = leg.cleaner_id
                                                            ? (leg.stop_type === 'dropoff' ? 'Drop' : 'Pick up')
                                                            : '';
                                                        const place = [
                                                            leg.unit_number ? `Unit ${leg.unit_number}` : null,
                                                            (leg.building || '').trim() || null,
                                                        ].filter(Boolean).join(' · ');
                                                        const at = leg.completed_at
                                                            ? new Date(leg.completed_at).toLocaleTimeString('en-GB', {
                                                                hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai',
                                                            })
                                                            : null;
                                                        const hhmm = (v) => (v
                                                            ? new Date(v).toLocaleTimeString('en-GB', {
                                                                hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai',
                                                            })
                                                            : null);
                                                        /**
                                                         * ONE time per stop, and it is the time THAT STOP has to
                                                         * happen (2026-07-28). A drop-off is timed by when the
                                                         * cleaning STARTS — that is when she has to be there. A
                                                         * collection is timed by when it ENDS. Printing the whole
                                                         * window on both said nothing about either.
                                                         */
                                                        const isCollect = leg.stop_type === 'collect';
                                                        const window = isCollect
                                                            ? hhmm(leg.booking_end)
                                                            : hhmm(leg.booking_start);
                                                        const windowLabel = isCollect ? 'End' : 'Start';
                                                        // A TICK FOR WHAT IS DONE (2026-07-28).
                                                        // The position number only matters while a
                                                        // stop is still ahead of her; once it has
                                                        // happened the useful fact is that it has.
                                                        // Skipped is NOT done — it is also
                                                        // non-pending, so ticking it would report a
                                                        // collection that never happened.
                                                        const done = leg.status === 'done';
                                                        const skipped = leg.status === 'skipped';
                                                        return (
                                                            <li
                                                                key={leg.id}
                                                                className={'scheduling-driver-stop'
                                                                    + (done ? ' is-done' : '')
                                                                    + (skipped ? ' is-skipped' : '')}
                                                            >
                                                                <span
                                                                    className="scheduling-driver-stop-n"
                                                                    title={done ? 'Done' : skipped ? 'Skipped' : `Stop ${i + 1}`}
                                                                >
                                                                    {done ? '✓' : skipped ? '✕' : i + 1}
                                                                </span>
                                                                <span className="scheduling-driver-stop-what">
                                                                    {`${verb} ${who}`.trim()}
                                                                </span>
                                                                <span className="scheduling-driver-stop-where">{place}</span>
                                                                {/* When this stop is DUE — cleaning start for a
                                                                    drop, cleaning end for a collect. Distinct from
                                                                    `at` below, which is when the driver actually
                                                                    completed it and only exists once done. */}
                                                                {window ? (
                                                                    <span
                                                                        className="scheduling-driver-stop-window"
                                                                        title={isCollect
                                                                            ? `Cleaning ends ${window}`
                                                                            : `Cleaning starts ${window}`}
                                                                    >
                                                                        <em>{windowLabel}:</em> {window}
                                                                    </span>
                                                                ) : null}
                                                                {at ? (
                                                                    <span className="scheduling-driver-stop-at">{at}</span>
                                                                ) : null}
                                                            </li>
                                                        );
                                                    })}
                                                    {!driverStopsFor(route.id).length && (
                                                        <li className="scheduling-driver-stop is-empty">
                                                            No stops generated for this route yet.
                                                        </li>
                                                    )}
                                                </ol>
                                            ) : timeGroupsToShow.map((groupKey) => {
                                                const groupLabel = timeGroupLabels[groupKey];
                                                // Driver order INSIDE each time band. The bands
                                                // (Morning / Peak / Afternoon) are what makes this
                                                // board readable, so they stay — but within a band
                                                // the cards now run in the order the driver
                                                // actually visits them, matching her app and the
                                                // Copy text (2026-07-27).
                                                const list = orderByDriverSequence(
                                                    sortBookingsByAreaAndTime(
                                                        filterByTimeGroup(routeBookings, groupKey)
                                                    )
                                                );
                                                if (!list.length) return null;
                                                return (
                                                    <div
                                                        className="scheduling-time-group"
                                                        key={`r${route.id}-${groupKey}`}
                                                    >
                                                        <h4 className="scheduling-time-group-title">
                                                            {groupLabel}
                                                        </h4>
                                                        {list.map((b, index) =>
                                                            renderBookingCard(b, index, { showBathrooms: true, route })
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {!routeBookings.length && (
                                                <div className="scheduling-route-empty">
                                                    No bookings on this route yet.
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                {/* Anything no route claimed. Shown rather than hidden:
                                    a booking silently missing from every route is work
                                    nobody is driving to. */}
                                {routesLoaded && activeRouteId === 'all' && orphanBookings.length > 0 && (
                                    <div className="scheduling-bookings-group">
                                        <div className="scheduling-group-header-with-copy">
                                            <h3 className="scheduling-group-title">
                                                Not on any route ({orphanBookings.length})
                                            </h3>
                                        </div>
                                        {sortBookingsByAreaAndTime(orphanBookings).map((b, index) =>
                                            renderBookingCard(b, index, { showBathrooms: true })
                                        )}
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Right: cleaners / part-timers */}
                    <div className="scheduling-column scheduling-column-right">
                        <section className="scheduling-cleaner-card">
                            <div className="scheduling-cleanerStickyTop">
                            <div className="scheduling-card-header">
                                <h2 className="scheduling-card-title">Available cleaners</h2>
                                <span className="scheduling-chip-count">
                                    {unassignedCleanerSlots.length}/{totalAvailableCount} available
                                </span>
                            </div>

                            {/* 🔍 Global cleaner search */}
                            <div className="scheduling-search-wrapper">
                                <input
                                    type="text"
                                    className="scheduling-input scheduling-cleaner-search-input"
                                    placeholder="Search cleaners..."
                                    value={cleanerSearch}
                                    onChange={(e) => setCleanerSearch(e.target.value)}
                                />
                            </div>
                            </div>

                            {/* Unassigned list */}
                            <Droppable droppableId="unassigned">
                                {(provided, snapshot) => (
                                    <div
                                        className={
                                            'scheduling-cleaner-list' +
                                            (snapshot.isDraggingOver ? ' scheduling-cleaner-list--drag-over' : '')
                                        }
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {sortedUnassignedCleanerSlots.map((slot, index) => {
                                            const availability = availabilities.find(
                                                (a) => a.availability_id === slot.availability_id
                                            );
                                            const isAvailable = availability?.is_available ?? true;

                                            return (
                                                <Draggable
                                                    key={slot.id}
                                                    draggableId={slot.id}
                                                    index={index}
                                                >
                                                    {(dragProvided) => (
                                                        <div
                                                            className="scheduling-cleaner-row"
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                        >
                                                            <div className="scheduling-cleaner-main">
                                                                <span className="scheduling-cleaner-name" title={slot.name}>
                                                                    {shortCleanerName(slot.name)}
                                                                </span>
                                                                {availability?.is_part_timer && (
                                                                    <span className="scheduling-tag scheduling-tag-light">
                                                                        Part-time
                                                                    </span>
                                                                )}
                                                                {availability?.is_trial && (
                                                                    <span className="scheduling-tag scheduling-tag-light">
                                                                        Trial
                                                                    </span>
                                                                )}
                                                                {renderIdBadge(availability)}
                                                            </div>

                                                            <label className="scheduling-switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isAvailable}
                                                                    onChange={() =>
                                                                        handleToggleAvailability(
                                                                            availability.availability_id,
                                                                            isAvailable
                                                                        )
                                                                    }
                                                                />
                                                                <span className="scheduling-slider" />
                                                            </label>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>

                            {/* Already assigned cleaners lists – separated by area */}
                            {totalAssignedCleanersCount > 0 && (
                                <section className="scheduling-assigned-section">
                                    <div className="scheduling-card-header">
                                        <h3 className="scheduling-card-title">
                                            Already assigned cleaners
                                        </h3>
                                        <span className="scheduling-chip-count">
                                            {totalAssignedCleanersCount}
                                        </span>
                                    </div>

                                    <div className="scheduling-assigned-columns">
                                        {/* Downtown / Business Bay & others */}
                                        <div className="scheduling-assigned-column">
                                            <div className='scheduling-card-header'>
                                                <h3 className="scheduling-card-title">
                                                    Downtown Area
                                                </h3>
                                                <span className="scheduling-chip-count scheduling-chip-count--area">
                                                    {availableBefore5Group1}
                                                </span>
                                            </div>

                                            <Droppable droppableId="assigned-pool-group1">
                                                {(provided, snapshot) => (
                                                    <div
                                                        className="scheduling-cleaner-list scheduling-cleaner-list-assigned"
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                    >
                                                        {filteredAssignedCleanersGroup1.map((slot, index) => {
                                                            const availability = availabilities.find(
                                                                (a) => a.availability_id === slot.availability_id
                                                            );

                                                            return (
                                                                <Draggable
                                                                    key={`assigned-pool-g1-${slot.availability_id || slot.id
                                                                        }`}
                                                                    draggableId={`assigned-pool-g1-${slot.availability_id || slot.id
                                                                        }`}
                                                                    index={index}
                                                                >
                                                                    {(dragProvided) => (
                                                                        <div
                                                                            className="scheduling-cleaner-row scheduling-cleaner-row-assigned scheduling-cleaner-row-assigned--group1"
                                                                            ref={dragProvided.innerRef}
                                                                            {...dragProvided.draggableProps}
                                                                            {...dragProvided.dragHandleProps}
                                                                        >
                                                                            <div className="scheduling-cleaner-main">
                                                                                <div className="scheduling-cleaner-name-line">
                                                                                    <span className="scheduling-cleaner-name" title={slot.name}>
                                                                                        {shortCleanerName(slot.name)}
                                                                                    </span>

                                                                                    {availability?.is_part_timer && (
                                                                                        <span className="scheduling-tag scheduling-tag-light">
                                                                                            Part-time
                                                                                        </span>
                                                                                    )}
                                                                                    {availability?.is_trial && (
                                                                                        <span className="scheduling-tag scheduling-tag-light">
                                                                                            Trial
                                                                                        </span>
                                                                                    )}

                                                                                    {renderIdBadge(availability)}
                                                                                </div>

                                                                                {slot.latestEndLabel && (() => {
                                                                                    const isLate =
                                                                                        slot.latestEnd != null &&
                                                                                        slot.latestEnd >= 17;

                                                                                    return (
                                                                                        <span
                                                                                            className={
                                                                                                'scheduling-cleaner-available-from' +
                                                                                                (isLate
                                                                                                    ? ' scheduling-cleaner-available-from--late'
                                                                                                    : '')
                                                                                            }
                                                                                        >
                                                                                            Available from {slot.latestEndLabel}
                                                                                        </span>
                                                                                    );
                                                                                })()}
                                                                                {cleanerStateById[slot.cleaner_id] && (
                                                                                    <span
                                                                                        className={'scheduling-progressChip scheduling-progressChip--'
                                                                                            + cleanerStateById[slot.cleaner_id].state}
                                                                                    >
                                                                                        {cleanerStateById[slot.cleaner_id].label}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            );
                                                        })}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>

                                        {/* Marina / JBR / JLT / Palm / JVC */}
                                        <div className="scheduling-assigned-column">
                                            <div className='scheduling-card-header'>
                                                <h3 className="scheduling-card-title">
                                                    Marina Area
                                                </h3>
                                                <span className="scheduling-chip-count scheduling-chip-count--area">
                                                    {availableBefore5Group2}
                                                </span>
                                            </div>
                                            <Droppable droppableId="assigned-pool-group2">
                                                {(provided, snapshot) => (
                                                    <div
                                                        className="scheduling-cleaner-list scheduling-cleaner-list-assigned"
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                    >
                                                        {filteredAssignedCleanersGroup2.map((slot, index) => {
                                                            const availability = availabilities.find(
                                                                (a) => a.availability_id === slot.availability_id
                                                            );

                                                            return (
                                                                <Draggable
                                                                    key={`assigned-pool-g2-${slot.availability_id || slot.id
                                                                        }`}
                                                                    draggableId={`assigned-pool-g2-${slot.availability_id || slot.id
                                                                        }`}
                                                                    index={index}
                                                                >
                                                                    {(dragProvided) => (
                                                                        <div
                                                                            className="scheduling-cleaner-row scheduling-cleaner-row-assigned scheduling-cleaner-row-assigned--group2"
                                                                            ref={dragProvided.innerRef}
                                                                            {...dragProvided.draggableProps}
                                                                            {...dragProvided.dragHandleProps}
                                                                        >

                                                                            <div className="scheduling-cleaner-main">
                                                                                <div className="scheduling-cleaner-name-line">
                                                                                    <span className="scheduling-cleaner-name" title={slot.name}>
                                                                                        {shortCleanerName(slot.name)}
                                                                                    </span>

                                                                                    {availability?.is_part_timer && (
                                                                                        <span className="scheduling-tag scheduling-tag-light">
                                                                                            Part-time
                                                                                        </span>
                                                                                    )}
                                                                                    {availability?.is_trial && (
                                                                                        <span className="scheduling-tag scheduling-tag-light">
                                                                                            Trial
                                                                                        </span>
                                                                                    )}

                                                                                    {renderIdBadge(availability)}
                                                                                </div>

                                                                                {slot.latestEndLabel && (() => {
                                                                                    const isLate =
                                                                                        slot.latestEnd != null &&
                                                                                        slot.latestEnd >= 17;

                                                                                    return (
                                                                                        <span
                                                                                            className={
                                                                                                'scheduling-cleaner-available-from' +
                                                                                                (isLate
                                                                                                    ? ' scheduling-cleaner-available-from--late'
                                                                                                    : '')
                                                                                            }
                                                                                        >
                                                                                            Available from {slot.latestEndLabel}
                                                                                        </span>
                                                                                    );
                                                                                })()}
                                                                                {cleanerStateById[slot.cleaner_id] && (
                                                                                    <span
                                                                                        className={'scheduling-progressChip scheduling-progressChip--'
                                                                                            + cleanerStateById[slot.cleaner_id].state}
                                                                                    >
                                                                                        {cleanerStateById[slot.cleaner_id].label}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            );
                                                        })}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    </div>
                                </section>
                            )}


                            {/* Part-time select */}
                            <form
                                onSubmit={handleCreatePartTimer}
                                className="scheduling-parttimer-form"
                            >
                                <label className="scheduling-label">
                                    Add part-time cleaner
                                </label>
                                <div className="scheduling-parttimer-row">
                                    <CreatableSelect
                                        className="scheduling-select"
                                        classNamePrefix="react-select"
                                        options={partTimeOptions}
                                        value={selectedPartTimer}
                                        onChange={setSelectedPartTimer}
                                        placeholder="Add Part-time Cleaner"
                                        isClearable
                                        onCreateOption={(inputValue) => {
                                            const newOption = {
                                                value: `new-${inputValue}`,
                                                label: inputValue,
                                            };
                                            setSelectedPartTimer(newOption);
                                        }}
                                    />
                                    <button
                                        type="submit"
                                        className="scheduling-primary-button"
                                        disabled={isCreatingPartTimer || !selectedPartTimer}
                                    >
                                        {isCreatingPartTimer ? 'Adding...' : 'Add'}
                                    </button>
                                </div>
                            </form>

                            {/* Unavailable full-time cleaners */}
                            {unavailableFullTimeCleaners.length > 0 && (
                                <section className="scheduling-unavailable-section">
                                    <div className="scheduling-card-header">
                                        <h3 className="scheduling-card-title">
                                            Unavailable cleaners
                                        </h3>
                                        <span className="scheduling-chip-count">
                                            {unavailableFullTimeCleaners.length}
                                        </span>
                                    </div>
                                    <p className="scheduling-card-subtitle">
                                        Full-time cleaners marked as not available today. Toggle to
                                        make them available again.
                                    </p>

                                    <div className="scheduling-cleaner-list scheduling-cleaner-list-unavailable">
                                        {filteredUnavailableFullTimeCleaners.map((a) => (
                                            <div
                                                key={`unavail-${a.availability_id}`}
                                                className="scheduling-cleaner-row scheduling-cleaner-row-unavailable"
                                            >
                                                <div className="scheduling-cleaner-main">
                                                    <span className="scheduling-cleaner-name" title={a.name}>
                                                        {shortCleanerName(a.name)}
                                                    </span>
                                                    {a.is_trial && (
                                                        <span className="scheduling-tag scheduling-tag-light">
                                                            Trial
                                                        </span>
                                                    )}
                                                    {renderIdBadge(a)}
                                                </div>
                                                <label className="scheduling-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={a.is_available}
                                                        onChange={() =>
                                                            handleToggleAvailability(
                                                                a.availability_id,
                                                                a.is_available
                                                            )
                                                        }
                                                    />
                                                    <span className="scheduling-slider" />
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </section>
                    </div>
                </div >
            </DragDropContext>

            <RouteDialog
                areaOptions={[...new Set(schedulableBookings.map((b) => b.area).filter(Boolean))]
                    // Never offer an area that a standing route already IS.
                    // "Downtown Dubai" and "Marina" belong to Downtown and
                    // Marina; picking them would create a route that fights the
                    // one it duplicates. The other areas inside those groups —
                    // JLT, JBR, Palm, Business Bay — stay selectable, because
                    // carving one of them off is exactly how a third driver
                    // gets their own run.
                    .filter((a) => !DEFAULT_ROUTE_SEED.some(
                        (d) => a.toLowerCase() === d.name.toLowerCase()
                            || a.toLowerCase().startsWith(`${d.name.toLowerCase()} `)
                    ))
                    .filter((a) => !routes.some((r) => r.route_name.toLowerCase() === a.toLowerCase()))
                    .sort()
                    .map((a) => ({ value: a, label: a, isArea: true }))}
                suggestion={AREA_SUGGESTIONS.find(
                    (a) => !routes.some((r) => r.route_name.toLowerCase() === a.toLowerCase())
                ) || ''}
                mode={routeDialog?.mode}
                route={routeDialog?.route}
                bookingCount={routeDialog?.route ? bookingsOnRoute(routeDialog.route.id) : 0}
                busy={routeDialogBusy}
                onSubmit={submitRouteDialog}
                onClose={() => { if (!routeDialogBusy) setRouteDialog(null); }}
            />
            {editBookingId && (
                <EditBooking
                    bookingId={editBookingId}
                    onClose={() => {
                        setEditBookingId(null);
                        loadData(); // refresh schedule after editing
                    }}
                />
            )}

            {permitModalBooking && (
                <SchedulingPermits
                    booking={permitModalBooking}
                    onClose={() => setPermitModalBooking(null)}
                    onBookingUpdated={(updatedFields) => {
                        setBookings((prev) =>
                            prev.map((bk) =>
                                bk.id === permitModalBooking.id
                                    ? { ...bk, ...updatedFields }
                                    : bk
                            )
                        );
                    }}
                />
            )}
        </div>
    );
}

export default Scheduling;